import { Button, Card, ConfirmDialog, useToast } from '@citizen-walk/shared-ui';
import {
  categoryMasterMatchesMode,
  DEFAULT_POST_ANALYSIS_KEY,
  isDefaultPostAnalysis,
  listPostAnalysisOptions,
  resolvePostAnalysisAiConfig,
  getModeLabel,
  getModeTemplate,
  normalizeAiOutputFormat,
  resolveEffectiveAiAnalysis,
  type AiAggregateScoresDef,
  type AiAnalysisSettings,
  type AiAssessmentDef,
  type AiCategoryMaster,
  type AiClassificationAxis,
  type AiGenerationConfig,
  type AiOutputFieldDef,
  type AiOutputFormat,
  type AiOutputFormatSchema,
  type AiOutputObjectArrayFieldDef,
  type AiPromptInputMapping,
  type AiPromptSections,
  type AiTitleGenerationConfig,
  type AiValidatorRule,
  type DistrictMode,
  type FieldDef,
  type ResolvedAiAnalysis,
} from '@citizen-walk/shared-types';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  setDistrictAiAnalysis,
  setDistrictPostAnalysisOverrides,
  useDistrict,
  useDistricts,
} from '../districts-admin';
import {
  archiveMaster,
  unarchiveMaster,
  upsertMaster,
  useAiAnalysisSettings,
  useAiCategoryMasters,
  useAiOutputFormats,
  useMasters,
} from './useMasters';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';
import { OverrideField } from './aiOverrideField';
import { ModeSelectField } from './components/ModeSelectField';
// 使用状況の pill バッジ (M3 出力フォーマット一覧と共用)。
import { UsageBadges } from './components/UsageBadges';
import {
  buildAiPayloadPreview,
  checkProfileIntegrity,
  type AiPayloadPreview,
  type IntegrityWarning,
} from './aiProfilePreview';
import { useModeProfileIntegrity } from './useModeProfileIntegrity';
import {
  GenerationConfigForm,
  TitleGenerationForm,
  ValidatorRulesForm,
  effectiveModelName,
  validateRules,
  FIELD_CLASS,
} from './aiM1Subforms';
import {
  FieldEditor,
  cleanField,
  cloneField,
  emptyChildField,
  emptyFieldOfKind,
  INPUT_CLASS,
  type ChildFieldKind,
  type OutputFieldKind,
} from './aiOutputFieldsEditor';
import {
  autoPromptInputMapping,
  buildDistrictConfigPayload,
  duplicateDraft,
  emptyOverrideState,
  expandInputFieldKeys,
  isM3SchemaDirty,
  groupDistrictsByTemplate,
  normalizeInputFieldKeys,
  overrideStateFromConfig,
  selectablePostFields,
  uniqueSlug,
  validateDocumentId,
  validateGenerationConfigMeta,
  validateGenerationConfigNumbers,
  type DistrictOverrideState,
  type OverrideFieldKey,
  type TemplateUsageDistrict,
} from './aiAnalysisEditorHelpers';

/**
 * AI 解析「統合エディタ」本実装 (テンプレ/M1 モード、Phase 3、2026-06-01)。
 *
 * POC (AiAnalysisProfilePage) を本番化したもの。M1 (aiAnalysisSettings) を中心に、紐付く
 * M2 (aiCategoryMasters) / M3 (aiOutputFormats) を inline で編集し、右 sticky ペインに
 * プロンプト全文 / JSON skeleton / 整合性 warnings を live 表示する。
 *
 * モード (Phase 4、2026-06-01):
 *   - テンプレモード (`districtId` なし): M1 を直接 CRUD (従来挙動、回帰させない)。
 *   - 地区モード (`districtId` あり): district doc を読み、`aiAnalysis.settingsId` で参照テンプレ (M1) を
 *     解決し、`resolveEffectiveAiAnalysis(m1, district.aiAnalysis)` で実効値 + 由来 (template/override) を
 *     得る。draft は実効値で初期化、各「項目」に override ON/OFF トグル (DistrictOverrideState) を持つ。
 *     継承中はグレー (淡色) 表示 + 入力 disabled、ON で通常色編集可。保存は ON 項目だけ詰めた
 *     DistrictAiAnalysisConfig を setDistrictAiAnalysis で district.aiAnalysis に全置換書込する。
 *     `rules` セクションはマスタ専管 (🔒 トグル無し)。実効値解決は shared-types の単一リゾルバを使う
 *     (独自マージ禁止 = 二重実装回避)。
 *
 * レイアウト = 縦アコーディオン (8 節) + 右 sticky ライブプレビュー:
 *   1. Gemini のバージョンとパラメータ (generationConfig — 先頭 = バージョンで以降の項目が変わるため)
 *   2. 基本情報 (表示名 / mode / id=slug 自動)
 *   3. プロンプト (promptSections 8 欄、自動挿入チップ + legacy 変換)
 *   4. AI に送る投稿データ (getModeTemplate(mode).dataSettings.postFields チェックリスト → inputFieldKeys)
 *   5. 参照カテゴリマスタ (同 mode の M2 複数選択 → categoryMasterIds)
 *   6. 出力フォーマット (M3 select + 選択中 M3 schema インライン編集、保存時 M3 upsert)
 *   7. 出力検証 (validators rules インライン)
 *   8. タイトル自動生成 (titleGeneration)
 *
 * 地区モードは「2. 基本情報」節を持たない (テンプレ専管) ため 7 節構成で、2 以降の番号が 1 つ
 * 繰り上がる (1. Gemini のバージョンとパラメータ → 2. プロンプト → … → 7. タイトル自動生成)。
 * 見出し番号は sectionNo(templateNo, dctx) で両モードを整合させる。
 *
 * key 手入力は全節で廃止 (要件4): id=slug 自動 / source=postFields 選択から自動 /
 * masterId・valueFrom・kind・fieldPath=select / fields[].key=候補補助。
 *
 * 保存: M1 を upsertMaster で保存し、M3 をインライン編集した場合は M3 も差分時のみ upsert する。
 * Firestore undefined 拒否罠 (commit 079aa32 同種) は cleanField / 条件付き付与で回避する。
 */

/** promptSections の 8 欄定義 (順序 = AI_PROMPT_SECTION_KEYS、buildPrompt の自動アセンブル順)。 */
const PROMPT_SECTION_FIELDS: {
  key: keyof AiPromptSections;
  /** アセンブル順番号 (1 始まり)。 */
  order: number;
  label: string;
  placeholder: string;
  /** この欄の直後に自動挿入されるもの (灰色チップ表示)。 */
  autoInsertAfter?: string;
  /** ロック (編集不可) セクションか (rules)。 */
  locked?: boolean;
}[] = [
  { key: 'role', order: 1, label: '役割 (role)', placeholder: 'あなたは〜の専門家です。' },
  {
    key: 'task',
    order: 2,
    label: 'タスク (task)',
    placeholder: '画像と入力から〜を解析してください。',
    autoInsertAfter: '投稿データ / 参照マスタ',
  },
  {
    key: 'contentRules',
    order: 3,
    label: '内容ルール (contentRules) — 各項目の書き方・長さ・理由の根拠',
    placeholder:
      'emotion_reason: 選んだ感情がなぜ妥当か画像の具体要素と結び付けて述べる。\ncategory_reason: 選んだ対象分類が投稿者の意図/画像のどの部分に対応するか述べる。',
  },
  {
    key: 'classificationRules',
    order: 4,
    label: '分類ルール (classificationRules)',
    placeholder: 'マスタから最も近いコードを選ぶ。',
  },
  {
    key: 'cautions',
    order: 5,
    label: '注意点 (cautions)',
    placeholder: '推測を断定的に書かない。',
  },
  {
    key: 'rules',
    order: 6,
    label: '厳格出力ルール (rules) — ロック / 編集不可 (マスタ管理範囲)',
    placeholder: '(既定の厳格出力ルールに追記。地区 override 不可。)',
    locked: true,
  },
  {
    key: 'outputSchemaNotes',
    order: 7,
    label: '出力スキーマ補足 (outputSchemaNotes)',
    placeholder: '',
    autoInsertAfter: '出力スキーマ',
  },
  {
    key: 'extraNotes',
    order: 8,
    label: '補足 (extraNotes、末尾)',
    placeholder: '',
    autoInsertAfter: 'チェックリスト',
  },
];

/**
 * 出力フォーマット (M3) の編集 draft。M3 専用ページの FormDraft を踏襲しつつ、統合エディタでは
 * 既存 M3 doc を起点に inline 編集する (新規 M3 作成は今回スコープ外、既存 M3 の編集のみ)。
 */
interface M3Draft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  rootKey: string;
  classificationAxes: M3AxisDraft[];
  assessmentPositiveLabel: string;
  assessmentNegativeLabel: string;
  assessmentDefinition: string;
  assessmentLevels: string[];
  aggregatePositiveKey: string;
  aggregateNegativeKey: string;
  aggregateOverallKey: string;
  aggregateDefinition: string;
  reasoningField: string;
  customSchemaText: string;
  fields: AiOutputFieldDef[];
}

interface M3AxisDraft {
  codeField: string;
  nameField: string;
  subField: string;
  label: string;
  definition: string;
  role: '' | 'primary' | 'secondary';
}

interface ProfileDraft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  /** systemInstructions (legacy 一枚岩、読み取り専用表示 + 変換ソース)。 */
  systemInstructions: string;
  promptSections: AiPromptSections;
  /** inputFieldKeys (undefined=全送信 / []=送らない / 明示配列)。 */
  inputFieldKeys: string[] | undefined;
  categoryMasterIds: string[];
  outputFormatId: string;
  promptInputMapping: AiPromptInputMapping[];
  validatorRules: AiValidatorRule[];
  generationConfig: AiGenerationConfig | null;
  titleGeneration: AiTitleGenerationConfig | null;
}

/**
 * 地区モードで各 Section に渡す継承トグル文脈 (Phase 4)。
 * テンプレモードでは undefined を渡す (= 従来の通常編集)。
 */
interface DistrictSectionCtx {
  /** 各項目の override 状態 + プロンプトセクション override 状態。 */
  overrides: DistrictOverrideState;
  /** 項目トグル ON/OFF。OFF 時は draft を実効値 (テンプレ継承値) へ巻き戻す。 */
  toggleField: (key: OverrideFieldKey, next: boolean) => void;
  /** プロンプトセクショントグル ON/OFF。 */
  togglePromptSection: (key: keyof AiPromptSections, next: boolean) => void;
  /** resolveEffectiveAiAnalysis の実効値 + 由来 (source 表示・継承値巻戻し用)。 */
  resolved: ResolvedAiAnalysis;
}

/**
 * 「＋ Gemini のバージョンとパラメータを設定」の初期値。既定モデルは第3世代なので、その世代で
 * 使わない温度 / Top-P / Top-K は持たせない (世代を第2世代に切替えると入力欄が出る)。
 */
const DEFAULT_GENERATION_CONFIG: AiGenerationConfig = {
  generation: 'v3',
  model: 'gemini-3.5-flash-lite',
  maxOutputTokens: 4096,
  responseMimeType: 'application/json',
};

const EMPTY_DRAFT: ProfileDraft = {
  id: '',
  displayName: '',
  mode: 'ecw',
  systemInstructions: '',
  promptSections: {},
  inputFieldKeys: undefined,
  categoryMasterIds: [],
  outputFormatId: '',
  promptInputMapping: [
    { templateVar: 'title', source: 'title' },
    { templateVar: 'detail', source: 'comment' },
  ],
  validatorRules: [],
  generationConfig: { ...DEFAULT_GENERATION_CONFIG },
  titleGeneration: null,
};

function draftFromM1(item: AiAnalysisSettings & { id: string }): ProfileDraft {
  return {
    id: item.id,
    displayName: item.displayName,
    mode: item.mode,
    systemInstructions: item.systemInstructions ?? '',
    promptSections: item.promptSections ? { ...item.promptSections } : {},
    inputFieldKeys: item.inputFieldKeys === undefined ? undefined : [...item.inputFieldKeys],
    categoryMasterIds: [...item.categoryMasterIds],
    outputFormatId: item.outputFormatId,
    promptInputMapping: item.promptInputMapping ? [...item.promptInputMapping] : [],
    validatorRules: item.validators?.rules ? [...item.validators.rules] : [],
    generationConfig: item.generationConfig ?? { ...DEFAULT_GENERATION_CONFIG },
    titleGeneration: item.titleGeneration ?? null,
  };
}

/**
 * 地区モードの draft 初期化: 参照テンプレ (M1) + 実効値 (resolveEffectiveAiAnalysis) から組み立てる。
 * 各項目は「実効値」(継承中=テンプレ値、上書き中=地区 override 値) を初期表示する。
 * id / displayName / mode はテンプレ (M1) のものを表示用に保持する。
 */
function draftFromResolved(
  m1: AiAnalysisSettings & { id: string },
  resolved: ResolvedAiAnalysis,
): ProfileDraft {
  return {
    id: m1.id,
    displayName: m1.displayName,
    mode: m1.mode,
    systemInstructions: resolved.systemInstructions.value,
    promptSections: { ...resolved.promptSections.value },
    inputFieldKeys:
      resolved.inputFieldKeys.value === undefined ? undefined : [...resolved.inputFieldKeys.value],
    categoryMasterIds: [...resolved.categoryMasterIds.value],
    outputFormatId: resolved.outputFormatId.value ?? '',
    promptInputMapping: resolved.promptInputMapping.value.map((m) => ({ ...m })),
    validatorRules: resolved.validators.value.rules ? [...resolved.validators.value.rules] : [],
    generationConfig: { ...resolved.generationConfig.value },
    titleGeneration: resolved.titleGeneration.value ? { ...resolved.titleGeneration.value } : null,
  };
}

function m3AxisFromSchema(a: AiClassificationAxis): M3AxisDraft {
  return {
    codeField: a.codeField,
    nameField: a.nameField,
    subField: a.subField ?? '',
    label: a.label,
    definition: a.definition ?? '',
    role: a.role === 'primary' || a.role === 'secondary' ? a.role : '',
  };
}

function m3DraftFromItem(item: AiOutputFormat & { id: string }): M3Draft {
  const s = item.schema;
  return {
    id: item.id,
    displayName: item.displayName,
    mode: item.mode,
    rootKey: s.rootKey ?? '',
    classificationAxes: s.classificationAxes.map(m3AxisFromSchema),
    assessmentPositiveLabel: s.assessment.positiveLabel,
    assessmentNegativeLabel: s.assessment.negativeLabel,
    assessmentDefinition: s.assessment.definition ?? '',
    assessmentLevels: [...s.assessment.levels],
    aggregatePositiveKey: s.aggregateScores.positiveKey ?? '',
    aggregateNegativeKey: s.aggregateScores.negativeKey ?? '',
    aggregateOverallKey: s.aggregateScores.overallKey ?? '',
    aggregateDefinition: s.aggregateScores.definition ?? '',
    reasoningField: s.reasoningField,
    customSchemaText: s.customSchemaText ?? '',
    fields: s.fields ? s.fields.map(cloneField) : [],
  };
}

/** M3Draft → AiOutputFormatSchema (保存用、undefined を握らせない条件付き構築)。 */
function m3DraftToSchema(d: M3Draft): AiOutputFormatSchema {
  const cleanedAxes: AiClassificationAxis[] = [];
  for (const a of d.classificationAxes) {
    const codeField = a.codeField.trim();
    const nameField = a.nameField.trim();
    const label = a.label.trim();
    if (!codeField || !nameField || !label) continue;
    const axis: AiClassificationAxis = { codeField, nameField, label };
    const subField = a.subField.trim();
    if (subField) axis.subField = subField;
    const definition = a.definition.trim();
    if (definition) axis.definition = definition;
    if (a.role === 'primary' || a.role === 'secondary') axis.role = a.role;
    cleanedAxes.push(axis);
  }

  const assessment: AiAssessmentDef = {
    positiveLabel: d.assessmentPositiveLabel.trim(),
    negativeLabel: d.assessmentNegativeLabel.trim(),
    levels: d.assessmentLevels.map((l) => l.trim()).filter((l) => l.length > 0),
  };
  const assessmentDefinition = d.assessmentDefinition.trim();
  if (assessmentDefinition) assessment.definition = assessmentDefinition;

  const aggregateScores: AiAggregateScoresDef = {};
  if (d.aggregatePositiveKey.trim()) aggregateScores.positiveKey = d.aggregatePositiveKey.trim();
  if (d.aggregateNegativeKey.trim()) aggregateScores.negativeKey = d.aggregateNegativeKey.trim();
  if (d.aggregateOverallKey.trim()) aggregateScores.overallKey = d.aggregateOverallKey.trim();
  const aggregateDefinition = d.aggregateDefinition.trim();
  if (aggregateDefinition) aggregateScores.definition = aggregateDefinition;

  const schema: AiOutputFormatSchema = {
    classificationAxes: cleanedAxes,
    assessment,
    aggregateScores,
    reasoningField: d.reasoningField.trim(),
  };
  if (d.rootKey.trim()) schema.rootKey = d.rootKey.trim();
  if (d.customSchemaText.trim()) schema.customSchemaText = d.customSchemaText;
  // fields[] は cleanField を通し、非空のときだけ書く (空配列で既存 format を破壊しない罠回避)。
  const cleanedFields = d.fields.map(cleanField);
  if (cleanedFields.length > 0) schema.fields = cleanedFields;
  return schema;
}

export interface AiAnalysisEditorPageProps {
  /**
   * Phase4 地区モード: 指定すると当該地区の `aiAnalysis` を編集 (テンプレ継承グレー + 項目ごと上書き)。
   * 未指定ならテンプレ (M1) モード (従来挙動)。
   */
  districtId?: string;
  /** 実 master uid (App から AdminAuthContext.user.uid を渡す。未指定なら 'admin-stub')。 */
  uid?: string;
}

/**
 * モード分岐エントリ。`districtId` があれば地区モード、無ければテンプレモードを描画する。
 */
/**
 * M1 (aiAnalysisSettings) の JSON import validator (pass-through 型ガード)。
 * M3 の validateAiOutputFormat と同方針: id/displayName/mode の最小チェックのみで、
 * promptSections / systemInstructions / generationConfig / categoryMasterIds / outputFormatId /
 * validators / titleGeneration / inputFieldKeys / promptInputMapping 等は verbatim 保持 (項目欠落させない)。
 */
export function validateAiAnalysisSettings(
  raw: unknown,
): (AiAnalysisSettings & { id: string }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id.trim()) return null;
  if (typeof r.displayName !== 'string') return null;
  if (typeof r.mode !== 'string') return null;
  return r as unknown as AiAnalysisSettings & { id: string };
}

export function AiAnalysisEditorPage(props: AiAnalysisEditorPageProps) {
  if (props.districtId) {
    return <DistrictAiEditor districtId={props.districtId} uid={props.uid} />;
  }
  return <TemplateAiEditor uid={props.uid} />;
}

function TemplateAiEditor(props: { uid?: string }) {
  const uid = props.uid?.trim() || 'admin-stub';
  // テンプレ一覧でアーカイブ済も表示 + 復元するため filterArchived:false で全件取得する
  // (編集ドロップダウンには下で active のみを派生して渡す)。
  const m1State = useMasters<AiAnalysisSettings & { id: string }>({
    collectionName: 'aiAnalysisSettings',
    filterArchived: false,
  });
  const m2State = useAiCategoryMasters();
  const m3State = useAiOutputFormats();
  const districtsState = useDistricts();
  const { toast } = useToast();

  const [selectedId, setSelectedId] = useState<string | ''>('');
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  /** M3 をインライン編集中の draft (outputFormatId 選択で読込)。 */
  const [m3Draft, setM3Draft] = useState<M3Draft | null>(null);
  /** M3 編集 draft の元 schema (dirty 判定用)。 */
  const [m3Original, setM3Original] = useState<AiOutputFormatSchema | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  /** アーカイブ確認ダイアログ対象 (null=閉じる)。 */
  const [archiveTarget, setArchiveTarget] = useState<(AiAnalysisSettings & { id: string }) | null>(
    null,
  );
  const [archiving, setArchiving] = useState(false);
  /**
   * 「別名で保存」直後 (未保存の複製) の複製元テンプレ表示名 (null=複製状態でない)。
   * 複製注記 (出力フォーマット共有の注意) の表示条件に使う。保存 / 読込切替でクリアする。
   */
  const [duplicatedFrom, setDuplicatedFrom] = useState<string | null>(null);
  /**
   * 新規作成中のドキュメント ID を管理者が手で編集したか。
   * true の間は表示名を変えても ID を自動生成し直さない (手入力を上書きしないため)。
   * 「表示名から自動生成」ボタン / テンプレ読込 / 複製 / 保存確定で false に戻す。
   */
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);
  /**
   * ドキュメント ID の入力欄に一度でも触れたか (入力 / フォーカスを外す / 保存を試みる)。
   * 触る前の空欄に「未入力」の赤字を出すと、開いた瞬間から画面が壊れて見えるため、
   * 未入力エラーの表示条件に使う (不正文字・重複は触った結果なので即時表示する)。
   */
  const [idTouched, setIdTouched] = useState(false);

  // status-conditional は毎 render で新配列を生成するため useMemo で安定化
  // (下流 useMemo の依存が毎 render 変わるのを防ぐ、react-hooks/exhaustive-deps)。
  /** 全テンプレ (アーカイブ済込み、一覧テーブル用)。 */
  const allM1Items = useMemo(() => (m1State.status === 'ready' ? m1State.items : []), [m1State]);
  /** 有効テンプレ (アーカイブ未、編集ドロップダウン用)。 */
  const m1Items = useMemo(() => allM1Items.filter((m) => !m.archivedAt), [allM1Items]);
  const districts = useMemo(
    () => (districtsState.status === 'ready' ? districtsState.items : []),
    [districtsState],
  );
  /** テンプレ settingsId → 使用地区 (一覧の使用地区列 + アーカイブ安全判定)。 */
  const districtsByTemplate = useMemo(() => groupDistrictsByTemplate(districts), [districts]);
  const m2Items = useMemo(() => (m2State.status === 'ready' ? m2State.items : []), [m2State]);
  const m3Items = useMemo(() => (m3State.status === 'ready' ? m3State.items : []), [m3State]);

  /**
   * 新規 id (slug) の衝突判定に使う既存 id 集合。
   * **アーカイブ済も必ず含める** (allM1Items)。新規保存は merge:false の全置換なので、
   * アーカイブ済と同 id を採番するとそのテンプレを無警告で上書き消滅させてしまう。
   */
  const existingIds = useMemo(() => allM1Items.map((m) => m.id), [allM1Items]);

  const selectedM3 = useMemo(
    () => (draft ? m3Items.find((m) => m.id === draft.outputFormatId) : undefined),
    [draft, m3Items],
  );
  const linkedM2s = useMemo(
    () => (draft ? m2Items.filter((m) => draft.categoryMasterIds.includes(m.id)) : []),
    [draft, m2Items],
  );

  // テンプレ mode の postFields (AI に送る投稿データ候補、要件5)。
  const selectablePostFieldList: FieldDef[] = useMemo(
    () => (draft ? selectablePostFields(getModeTemplate(draft.mode).dataSettings.postFields) : []),
    [draft],
  );
  const selectableKeys = useMemo(
    () => selectablePostFieldList.map((f) => f.key),
    [selectablePostFieldList],
  );
  const checkedInputKeys = useMemo(
    () => (draft ? expandInputFieldKeys(draft.inputFieldKeys, selectableKeys) : []),
    [draft, selectableKeys],
  );

  // 右ペイン: M3 編集中ならその draft schema をプレビューに反映 (未保存でも live 確認できる)。
  const previewM3: (AiOutputFormat & { id: string }) | undefined = useMemo(() => {
    if (!selectedM3) return undefined;
    if (m3Draft && m3Draft.id === selectedM3.id) {
      return { ...selectedM3, schema: m3DraftToSchema(m3Draft) };
    }
    return selectedM3;
  }, [selectedM3, m3Draft]);

  const payloadPreview = useMemo<AiPayloadPreview | null>(() => {
    if (!draft) return null;
    return buildAiPayloadPreview({
      mode: draft.mode,
      systemInstructions: draft.systemInstructions,
      promptSections: draft.promptSections,
      promptInputMapping: draft.promptInputMapping,
      inputFieldKeys: draft.inputFieldKeys,
      generationConfig: draft.generationConfig,
      validatorRules: draft.validatorRules,
      titleGeneration: draft.titleGeneration,
      m2List: linkedM2s,
      m3Schema: previewM3?.schema,
      postFields: selectablePostFieldList,
    });
  }, [draft, linkedM2s, previewM3, selectablePostFieldList]);

  const modeIntegrity = useModeProfileIntegrity(draft?.mode);
  const warnings = useMemo(() => {
    if (!draft) return [] as IntegrityWarning[];
    return checkProfileIntegrity({
      mode: draft.mode,
      m1: {
        outputFormatId: draft.outputFormatId,
        categoryMasterIds: draft.categoryMasterIds,
        validatorRules: draft.validatorRules,
        promptInputMapping: draft.promptInputMapping,
        systemInstructions: draft.systemInstructions,
      },
      m2List: m2Items,
      m3: previewM3,
      // mode 文字列の直接比較ではなく ModeProfile (M10) の推奨構成を正とする (issue #45)
      recommendedMasterIds: modeIntegrity.recommendedMasterIds,
      resolveModeLabel: modeIntegrity.resolveModeLabel,
    });
  }, [draft, m2Items, previewM3, modeIntegrity]);

  function loadM3Draft(outputFormatId: string) {
    const m3 = m3Items.find((m) => m.id === outputFormatId);
    if (m3) {
      setM3Draft(m3DraftFromItem(m3));
      setM3Original(m3.schema);
    } else {
      setM3Draft(null);
      setM3Original(undefined);
    }
  }

  function handleLoad(id: string) {
    setSelectedId(id);
    setDuplicatedFrom(null); // 別のテンプレを読み込んだら複製状態は解除
    setIdManuallyEdited(false); // 読込のたびに ID の自動生成モードへ戻す
    setIdTouched(false); // 未タッチ扱いに戻す (開いた直後の空欄で赤字を出さない)
    if (!id) {
      setDraft(null);
      setM3Draft(null);
      setM3Original(undefined);
      return;
    }
    if (id === '__new__') {
      setDraft({ ...EMPTY_DRAFT, promptInputMapping: [...EMPTY_DRAFT.promptInputMapping] });
      setM3Draft(null);
      setM3Original(undefined);
      return;
    }
    const item = m1Items.find((m) => m.id === id);
    if (item) {
      const d = draftFromM1(item);
      setDraft(d);
      loadM3Draft(d.outputFormatId);
    }
  }

  /**
   * 別名保存 (複製) の共通処理: 渡された draft の内容を丸ごと引き継いだ新規テンプレ draft を用意する。
   * **この時点では保存しない** (誤操作で意図しないテンプレが増えないよう、確定は「保存」ボタン)。
   * 参照している出力フォーマット (M3) は複製されず元テンプレと共有される点を toast + 注記で伝える。
   */
  function applyDuplicate(source: ProfileDraft) {
    setDraft(duplicateDraft(source, existingIds));
    setSelectedId('__new__');
    setDuplicatedFrom(source.displayName);
    setIdManuallyEdited(false); // 複製直後は `{元 id}-copy` のまま。手入力すればそこで固定される
    setIdTouched(false);
    toast(
      'success',
      `「${source.displayName}」の内容を複製しました。名前とドキュメント ID を確認して「保存」を押してください。` +
        `出力フォーマットは元のテンプレートと共有されます。`,
    );
  }

  /** 編集中のテンプレを別名で保存する (未保存の編集内容も含めて引き継ぐ)。 */
  function handleDuplicateCurrent() {
    if (!draft) return;
    applyDuplicate(draft);
  }

  /** 一覧の行から複製する (その行を読み込んだうえで別名保存と同じ状態にする)。 */
  function handleDuplicateFromRow(m1: AiAnalysisSettings & { id: string }) {
    const d = draftFromM1(m1);
    loadM3Draft(d.outputFormatId);
    applyDuplicate(d);
  }

  /** アーカイブ済テンプレを復元 (archivedAt=null → 一覧の有効に戻る)。 */
  async function handleRestore(m1: AiAnalysisSettings & { id: string }) {
    try {
      await unarchiveMaster('aiAnalysisSettings', m1.id, uid);
      toast('success', `「${m1.displayName}」を復元しました`);
    } catch (e) {
      toast('error', `復元失敗: ${(e as Error).message}`);
    }
  }

  /** アーカイブ実行 (確認ダイアログの OK)。使用中でも許可 (admin の判断)。 */
  async function confirmArchive() {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await archiveMaster('aiAnalysisSettings', archiveTarget.id, uid);
      toast('success', `「${archiveTarget.displayName}」をアーカイブしました`);
      if (selectedId === archiveTarget.id) handleLoad(''); // 編集中だったら編集面を閉じる
      setArchiveTarget(null);
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    } finally {
      setArchiving(false);
    }
  }

  const isNew = selectedId === '__new__';

  /**
   * 表示名変更: 新規時かつ ID 未編集なら id を slug 自動生成する。
   * 管理者が ID を手入力済み (idManuallyEdited) のときは、その ID を上書きしない。
   */
  function setDisplayName(name: string) {
    if (!draft) return;
    if (isNew && !idManuallyEdited) {
      setDraft({ ...draft, displayName: name, id: uniqueSlug(name, existingIds) });
    } else {
      setDraft({ ...draft, displayName: name });
    }
  }

  /** ドキュメント ID の手入力 (新規時のみ)。以後は表示名変更で自動生成し直さない。 */
  function setDocumentId(id: string) {
    if (!draft) return;
    setIdManuallyEdited(true);
    setIdTouched(true);
    setDraft({ ...draft, id });
  }

  /** ドキュメント ID を表示名からの自動生成に戻す。 */
  function regenerateDocumentId() {
    if (!draft) return;
    setIdManuallyEdited(false);
    setDraft({ ...draft, id: uniqueSlug(draft.displayName, existingIds) });
  }

  /**
   * 新規作成中のドキュメント ID の問題 (未入力 / 使用不可文字 / 既存重複)。
   * 保存の可否判定に使う (最終防御は handleSave 側)。
   */
  const idError = useMemo(
    () => (draft && isNew ? validateDocumentId(draft.id, existingIds) : null),
    [draft, isNew, existingIds],
  );

  /**
   * 入力欄の下に赤字で出すエラー。
   *  - 使用できない文字 / ハイフンの位置 / 既存との重複 = 入力した結果なので即時表示する。
   *  - 未入力 = 一度も触っていない間は出さない (開いた直後から赤字だと壊れて見えるため)。
   *    入力欄に触れる / フォーカスを外す / 保存を試みる、のいずれかで表示に切り替わる。
   */
  const visibleIdError = useMemo(() => {
    if (idError == null) return null;
    if (!idTouched && !(draft?.id ?? '').trim()) return null;
    return idError;
  }, [idError, idTouched, draft]);

  function setMode(mode: DistrictMode) {
    if (!draft || mode === draft.mode) return;
    // mode 切替で M2 / M3 参照と inputFieldKeys が不整合になるため reset。
    setDraft({
      ...draft,
      mode,
      categoryMasterIds: [],
      outputFormatId: '',
      inputFieldKeys: undefined,
    });
    setM3Draft(null);
    setM3Original(undefined);
  }

  function toggleInputFieldKey(key: string, checked: boolean) {
    if (!draft) return;
    const cur = new Set(checkedInputKeys);
    if (checked) cur.add(key);
    else cur.delete(key);
    const nextSelected = selectableKeys.filter((k) => cur.has(k));
    const nextInputFieldKeys = normalizeInputFieldKeys(nextSelected, selectableKeys);
    // 選択に応じて promptInputMapping を裏で自動補完 (既存 mapping は尊重)。
    const nextMapping = autoPromptInputMapping(nextSelected, draft.promptInputMapping);
    setDraft({ ...draft, inputFieldKeys: nextInputFieldKeys, promptInputMapping: nextMapping });
  }

  function setAllInputFieldKeys(mode: 'all' | 'none') {
    if (!draft) return;
    if (mode === 'all') {
      setDraft({
        ...draft,
        inputFieldKeys: undefined,
        promptInputMapping: autoPromptInputMapping(selectableKeys, draft.promptInputMapping),
      });
    } else {
      setDraft({ ...draft, inputFieldKeys: [] });
    }
  }

  function toggleM2(id: string, checked: boolean) {
    if (!draft) return;
    const next = checked
      ? [...draft.categoryMasterIds.filter((x) => x !== id), id]
      : draft.categoryMasterIds.filter((x) => x !== id);
    setDraft({ ...draft, categoryMasterIds: next });
  }

  function setOutputFormatId(id: string) {
    if (!draft) return;
    setDraft({ ...draft, outputFormatId: id });
    loadM3Draft(id);
  }

  function convertLegacyToSections() {
    if (!draft) return;
    if (!draft.systemInstructions.trim()) {
      toast('error', 'legacy systemInstructions が空です');
      return;
    }
    // 単純変換: 全文を task セクションへ移す (admin が手で分割し直す出発点)。
    setDraft({
      ...draft,
      promptSections: { ...draft.promptSections, task: draft.systemInstructions },
    });
    toast('success', 'legacy 本文を task セクションへ変換しました (必要に応じて分割してください)');
  }

  /**
   * レガシー本文 (旧 systemInstructions) を削除する。
   * promptSections (新形式) が未設定のまま削除すると AI 解析の指示が空になるため、
   * 1 つ以上のセクションが入力済みのときのみ許可する (未設定なら「変換」を促す)。
   * 削除は draft 上の操作で、「保存」で Firestore に確定する (systemInstructions: '' を書込)。
   */
  function deleteLegacyContent() {
    if (!draft) return;
    if (!draft.systemInstructions.trim()) {
      toast('error', '削除するレガシー本文はありません');
      return;
    }
    const hasSections = PROMPT_SECTION_FIELDS.some(
      (f) => (draft.promptSections[f.key] ?? '').trim().length > 0,
    );
    if (!hasSections) {
      toast(
        'error',
        'プロンプトのセクションが未入力です。先に「v2 セクションへ変換」してから削除してください',
      );
      return;
    }
    if (
      !confirm(
        'レガシーの旧プロンプト本文を削除します。\n新形式のセクションで動作するため、表示・解析には影響しません。\n「保存」で確定します。よろしいですか?',
      )
    ) {
      return;
    }
    setDraft({ ...draft, systemInstructions: '' });
    toast('success', 'レガシー本文を削除しました。「保存」で確定してください');
  }

  async function handleSave() {
    if (!draft) return;
    // 保存を試みた = ドキュメント ID にも触れたとみなす (押せない理由が分からないまま、を避ける)。
    if (isNew) setIdTouched(true);
    if (!draft.displayName.trim()) {
      toast('error', '表示名は必須です');
      return;
    }
    // ドキュメント ID の最終防御 (UI 側の保存抑止だけに頼らない)。
    // 新規保存は merge:false の全置換なので、既存 (アーカイブ済含む) と同 id を通すと上書き消滅する。
    if (isNew) {
      const idErr = validateDocumentId(draft.id, existingIds);
      if (idErr) {
        toast('error', idErr);
        return;
      }
    }
    if (!draft.outputFormatId.trim()) {
      toast('error', '出力フォーマット (M3) は必須です');
      return;
    }
    // generationConfig 数値検証
    const gc = draft.generationConfig;
    if (!gc) {
      toast('error', 'Gemini パラメータ (generationConfig) は必須です');
      return;
    }
    // 世代で有効な数値項目だけを検証 (第3世代は温度 / Top-P / Top-K を持たない)。
    const gcNumbers = validateGenerationConfigNumbers(gc);
    if (!gcNumbers.ok) {
      toast('error', gcNumbers.error);
      return;
    }
    if (!gc.responseMimeType || !gc.responseMimeType.trim()) {
      toast('error', 'generationConfig.responseMimeType は必須です');
      return;
    }
    // model / thinkingConfig 検証 + 正規化 (空白のみの model・不正な thinkingLevel を弾く)。
    const gcMeta = validateGenerationConfigMeta(gc);
    if (!gcMeta.ok) {
      toast('error', gcMeta.error);
      return;
    }
    const cleanedGc = gcMeta.value;
    // validators
    const validatorsResult = validateRules(draft.validatorRules);
    if (!validatorsResult.ok) {
      toast('error', `検証ルール: ${validatorsResult.error}`);
      return;
    }
    // promptInputMapping: 空行除外、片方欠落はエラー
    const cleanedMapping: AiPromptInputMapping[] = [];
    for (let i = 0; i < draft.promptInputMapping.length; i += 1) {
      const m = draft.promptInputMapping[i]!;
      const tv = m.templateVar.trim();
      const src = m.source.trim();
      if (!tv && !src) continue;
      if (!tv || !src) {
        toast('error', `プロンプト変数マッピング 行 ${i + 1}: templateVar / source は両方必須です`);
        return;
      }
      cleanedMapping.push({ templateVar: tv, source: src });
    }
    // promptSections: 非空のみ残す
    const cleanedSections: AiPromptSections = {};
    for (const f of PROMPT_SECTION_FIELDS) {
      const v = draft.promptSections[f.key];
      if (typeof v === 'string' && v.trim()) cleanedSections[f.key] = v.trim();
    }
    const hasSections = Object.keys(cleanedSections).length > 0;

    setSaving(true);
    try {
      // M3 を inline 編集していて dirty なら先に M3 を保存 (差分時のみ)。
      if (m3Draft && selectedM3 && m3Draft.id === selectedM3.id) {
        const nextSchema = m3DraftToSchema(m3Draft);
        if (isM3SchemaDirty(m3Original, nextSchema)) {
          // M3 fields[] の key 必須 + 重複検証 (空行は cleanField で除外済みだが key 必須を明示)。
          const m3Err = validateM3Fields(m3Draft.fields);
          if (m3Err) {
            toast('error', `出力フォーマット: ${m3Err}`);
            setSaving(false);
            return;
          }
          await upsertMaster<AiOutputFormat>({
            collectionName: 'aiOutputFormats',
            id: m3Draft.id,
            uid,
            isNew: false,
            data: {
              mode: m3Draft.mode,
              displayName: m3Draft.displayName.trim(),
              schema: nextSchema,
              enabled: true,
              version: 1,
              schemaVersion: 1,
              archivedAt: null,
            },
          });
          setM3Original(nextSchema);
        }
      }

      const data: Omit<
        AiAnalysisSettings,
        'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
      > = {
        mode: draft.mode,
        displayName: draft.displayName.trim(),
        systemInstructions: draft.systemInstructions,
        categoryMasterIds: [...draft.categoryMasterIds],
        outputFormatId: draft.outputFormatId.trim(),
        generationConfig: cleanedGc,
        promptInputMapping: cleanedMapping,
        enabled: true,
        version: 1,
        schemaVersion: 1,
        archivedAt: null,
      };
      // 条件付き付与で undefined を握らせない (commit 079aa32 同種の罠回避)。
      if (draft.titleGeneration) data.titleGeneration = draft.titleGeneration;
      if (validatorsResult.value) data.validators = validatorsResult.value;
      if (hasSections) data.promptSections = cleanedSections;
      if (draft.inputFieldKeys !== undefined) data.inputFieldKeys = draft.inputFieldKeys;

      await upsertMaster<AiAnalysisSettings>({
        collectionName: 'aiAnalysisSettings',
        id: isNew ? draft.id.trim() : selectedId || undefined,
        uid,
        isNew,
        data,
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      if (isNew) {
        setSelectedId(draft.id.trim());
        setDuplicatedFrom(null); // 保存確定で複製状態を解除 (以降は通常の編集)
        setIdManuallyEdited(false); // 保存後は ID 編集不可 (既存テンプレ扱い) に戻る
        setIdTouched(false);
      }
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  /**
   * M1 を JSON で一括取込 (staging→本番の移送用)。
   * 同 id が既存 (アーカイブ済含む allM1Items) なら update、無ければ新規 create。
   * stripSystemFields で監査列を除去し、それ以外の全項目を verbatim で upsert する。
   */
  async function handleImport(importedItems: (AiAnalysisSettings & { id: string })[]) {
    for (const it of importedItems) {
      const existing = allM1Items.find((x) => x.id === it.id);
      const { id, ...rest } = stripSystemFields(it);
      await upsertMaster<AiAnalysisSettings>({
        collectionName: 'aiAnalysisSettings',
        id: id as string,
        uid,
        isNew: !existing,
        data: rest as Omit<
          AiAnalysisSettings,
          'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
        >,
      });
    }
  }

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">AI 解析 統合エディタ</h1>
        <p className="text-sm text-slate-500 mt-1">
          AI 解析設定 (M1) を中心に、プロンプト・投稿データ・参照マスタ (M2)・出力フォーマット
          (M3)・ 検証・タイトル生成・Gemini パラメータを 1 画面で編集します。右ペインに「実際に AI
          に渡る プロンプト」「期待される JSON 応答」「整合性 warnings」が live 表示されます。
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        {/* このエディタは組込モード (ecw/bousai/walker/kids-bousai) に加え、地区が ModeProfile
            経由で登録した custom モード (`DistrictMode = BuiltInDistrictMode | (string & {})`)
            のテンプレートも横断して扱う (staging→本番の移送用)。expectedMode を組込4モードに
            固定すると custom モードのテンプレートが軒並み skip されてしまう不具合があったため
            (2026-07-14 実機で発覚)、mode 値の制限は行わない (validateAiAnalysisSettings の
            構造チェックのみに委ねる)。 */}
        <MasterImportExportButtons<AiAnalysisSettings & { id: string }>
          collectionName="aiAnalysisSettings"
          items={m1Items}
          validator={validateAiAnalysisSettings}
          onImport={handleImport}
          disabled={saving}
        />
        <span className="text-xs text-slate-500">
          有効な全テンプレ (M1) を JSON で書き出し /
          取込（staging→本番の移送用）。アーカイブ済は各行の「📥 JSON」で個別に。
        </span>
      </div>

      <div className="mb-4">
        <TemplateUsageTable
          templates={allM1Items}
          districtsByTemplate={districtsByTemplate}
          selectedId={selectedId}
          onEdit={handleLoad}
          onDuplicate={handleDuplicateFromRow}
          onArchive={(m1) => setArchiveTarget(m1)}
          onRestore={(m1) => void handleRestore(m1)}
        />
      </div>

      <Card padding="md" className="mb-4">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-xs text-slate-600 mb-1" htmlFor="ai-editor-select">
              編集する AI 解析設定 (M1)
            </label>
            <select
              id="ai-editor-select"
              value={selectedId}
              onChange={(e) => handleLoad(e.target.value)}
              className={`${FIELD_CLASS} bg-white`}
            >
              <option value="">(選択してください)</option>
              <option value="__new__">＋ 新規作成</option>
              {m1Items.map((m) => {
                const em = effectiveModelName(m.generationConfig);
                return (
                  <option key={m.id} value={m.id}>
                    {m.displayName} ({m.id}、mode={m.mode}、モデル={em.model}
                    {em.isDefault ? ' (既定)' : ''})
                  </option>
                );
              })}
            </select>
          </div>
          <div className="text-xs text-slate-500 flex gap-3 flex-wrap">
            <Link to="/masters/ai-output-format" className="text-primary-600 hover:underline">
              → M3 一覧
            </Link>
            <Link to="/masters/ai-category" className="text-primary-600 hover:underline">
              → M2 一覧
            </Link>
          </div>
        </div>
      </Card>

      {!draft && (
        <Card padding="lg">
          <p className="text-sm text-slate-500">
            上のドロップダウンから既存の AI 解析設定を選ぶか、「＋ 新規作成」を選んでください。
          </p>
        </Card>
      )}

      {draft && duplicatedFrom != null && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <p className="font-bold">
            「{duplicatedFrom}」を複製した、まだ保存していないテンプレートです。
          </p>
          <p className="mt-1">
            表示名とドキュメント ID
            を確認して「保存」を押すと、新しいテンプレートとして作成されます。
          </p>
          <p className="mt-1">
            出力フォーマットは元のテンプレートと共有されます。ここで出力フォーマットを編集すると元のテンプレートにも反映されます。別の内容にしたいときは、
            <Link to="/masters/ai-output-format" className="underline">
              出力フォーマットの一覧
            </Link>
            で新しく作ってから、この画面で選び直してください。
          </p>
        </div>
      )}

      {draft && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(360px,40%)] gap-4 items-start">
          {/* ============ LEFT: accordion sections ============ */}
          <div className="grid grid-cols-1 gap-3">
            <SectionGenerationConfig draft={draft} setDraft={setDraft} />
            <SectionBasic
              draft={draft}
              setDraft={setDraft}
              setDisplayName={setDisplayName}
              setMode={setMode}
              isNew={isNew}
              setDocumentId={setDocumentId}
              onRegenerateDocumentId={regenerateDocumentId}
              idManuallyEdited={idManuallyEdited}
              idError={visibleIdError}
              onDocumentIdBlur={() => setIdTouched(true)}
            />
            <SectionPrompt
              draft={draft}
              setDraft={setDraft}
              onConvertLegacy={convertLegacyToSections}
              onDeleteLegacy={deleteLegacyContent}
            />
            <SectionInputData
              draft={draft}
              fields={selectablePostFieldList}
              checkedKeys={checkedInputKeys}
              onToggle={toggleInputFieldKey}
              onAll={() => setAllInputFieldKeys('all')}
              onNone={() => setAllInputFieldKeys('none')}
            />
            <SectionCategoryMasters draft={draft} m2Items={m2Items} onToggle={toggleM2} />
            <SectionOutput
              draft={draft}
              m3Items={m3Items}
              m3Draft={m3Draft}
              setM3Draft={setM3Draft}
              onSelect={setOutputFormatId}
            />
            <SectionValidators
              draft={draft}
              setDraft={setDraft}
              m2Items={m2Items}
              selectedM3={selectedM3}
            />
            <SectionTitleGeneration draft={draft} setDraft={setDraft} />

            <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => handleLoad('')}
                disabled={saving}
              >
                キャンセル
              </Button>
              {!isNew && selectedId !== '' && (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={handleDuplicateCurrent}
                  disabled={saving}
                  title="いま編集している内容を引き継いで、新しいテンプレートの下書きを作ります（この操作では保存されません）"
                >
                  別名で保存
                </Button>
              )}
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving || visibleIdError != null}
                title={
                  visibleIdError != null
                    ? 'ドキュメント ID を修正すると保存できます'
                    : '編集内容を保存します'
                }
              >
                {saving ? '保存中…' : '保存'}
              </Button>
            </div>
          </div>

          {/* ============ RIGHT: sticky live preview ============ */}
          <div className="lg:sticky lg:top-4 grid grid-cols-1 gap-3">
            <PreviewWarnings warnings={warnings} mode={draft.mode} />
            <PreviewPayload preview={payloadPreview} />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={archiveTarget != null}
        intent="warning"
        title="テンプレをアーカイブ"
        confirmLabel={archiving ? 'アーカイブ中…' : 'アーカイブする'}
        message={(() => {
          if (!archiveTarget) return '';
          const using = districtsByTemplate[archiveTarget.id] ?? [];
          return (
            <div className="grid grid-cols-1 gap-2">
              <p>
                「<strong>{archiveTarget.displayName}</strong>」(<code>{archiveTarget.id}</code>)
                をアーカイブします。アーカイブ済テンプレは地区の「使用する AI 解析設定」の選択肢から
                外れます（既存地区の解析は settingsId
                が残るため継続します）。後から「復元」できます。
              </p>
              {using.length > 0 ? (
                <div className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                  ⚠ このテンプレは <strong>{using.length} 地区</strong>で使用中です:{' '}
                  {using.map((d) => d.name).join('、')}
                  。アーカイブしても解析は止まりませんが、別テンプレへ付け替えるまで選択し直せません。
                </div>
              ) : (
                <p className="text-xs text-slate-500">このテンプレを使用中の地区はありません。</p>
              )}
            </div>
          );
        })()}
        onConfirm={() => void confirmArchive()}
        onCancel={() => setArchiveTarget(null)}
      />
    </div>
  );
}

/** テンプレ一覧 (使用地区 + アーカイブ/復元)。AI 解析設定 M1 を一覧で管理する (2026-06-01)。 */
export function TemplateUsageTable(props: {
  templates: (AiAnalysisSettings & { id: string })[];
  districtsByTemplate: Record<string, TemplateUsageDistrict[]>;
  selectedId: string | '';
  onEdit: (id: string) => void;
  /**
   * 行のテンプレを複製して「別名で保存」の下書きにする (アーカイブ済行では出さない
   * = 先に「復元」してから複製する)。押した時点では保存しない。
   */
  onDuplicate?: (m1: AiAnalysisSettings & { id: string }) => void;
  onArchive: (m1: AiAnalysisSettings & { id: string }) => void;
  onRestore: (m1: AiAnalysisSettings & { id: string }) => void;
}) {
  const { templates, districtsByTemplate, selectedId, onEdit, onDuplicate, onArchive, onRestore } =
    props;
  const active = templates.filter((m) => !m.archivedAt);
  const archived = templates.filter((m) => m.archivedAt);

  /**
   * 「実際に使われるモデル名」を表示する (model 未設定ならランタイム既定 + 「(既定)」注記)。
   * どのテンプレが旧モデル (gemini-2.5-flash 等) のままかを一覧で見分けるための列。
   */
  function renderModel(m: AiAnalysisSettings & { id: string }) {
    const { model, isDefault } = effectiveModelName(m.generationConfig);
    return (
      <>
        <span className="font-mono text-[11px]">{model}</span>
        {isDefault && <span className="text-[10px] text-slate-400 ml-1">(既定)</span>}
      </>
    );
  }

  function renderUsage(id: string) {
    const ds = districtsByTemplate[id] ?? [];
    // バッジの見た目は M3 (出力フォーマット) 一覧と共用 (components/UsageBadges)。
    return <UsageBadges items={ds.map((d) => ({ key: d.id, label: d.name }))} />;
  }

  return (
    <Card padding="lg">
      <h2 className="text-base font-bold text-slate-900 mb-1">
        テンプレ一覧（使用地区 / アーカイブ）
      </h2>
      <p className="text-xs text-slate-500 mb-3">
        各 AI 解析設定テンプレ (M1) を、どの地区が使用中か確認しながらアーカイブ / 復元できます。
        アーカイブ済は地区の「使用する AI 解析設定」選択肢から外れます（既存地区の解析は継続）。
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 font-medium">テンプレ</th>
              <th className="py-2 pr-3 font-medium">mode</th>
              <th className="py-2 pr-3 font-medium">モデル</th>
              <th className="py-2 pr-3 font-medium">使用地区</th>
              <th className="py-2 pr-3 font-medium">状態</th>
              <th className="py-2 pl-3 font-medium text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {active.length === 0 && archived.length === 0 && (
              <tr>
                <td colSpan={6} className="py-3 text-slate-400 text-xs">
                  テンプレがありません。下の「＋ 新規作成」で追加してください。
                </td>
              </tr>
            )}
            {active.map((m) => {
              const usingCount = (districtsByTemplate[m.id] ?? []).length;
              return (
                <tr
                  key={m.id}
                  className={`border-b border-slate-100 align-top ${
                    selectedId === m.id ? 'bg-primary-50/40' : ''
                  }`}
                >
                  <td className="py-2 pr-3">
                    <div className="font-medium text-slate-800">{m.displayName}</div>
                    <div className="text-[11px] text-slate-400 font-mono">{m.id}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs text-slate-600">{m.mode}</td>
                  <td className="py-2 pr-3 text-slate-600">{renderModel(m)}</td>
                  <td className="py-2 pr-3">{renderUsage(m.id)}</td>
                  <td className="py-2 pr-3">
                    <span className="text-xs text-emerald-700">有効</span>
                  </td>
                  <td className="py-2 pl-3">
                    <div className="flex gap-2 justify-end">
                      <RowExportButton collectionName="aiAnalysisSettings" item={m} />
                      <Button variant="secondary" size="sm" onClick={() => onEdit(m.id)}>
                        編集
                      </Button>
                      {onDuplicate && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onDuplicate(m)}
                          title="このテンプレートの内容を引き継いだ新しいテンプレートの下書きを作ります（この操作では保存されません）"
                        >
                          📋 複製
                        </Button>
                      )}
                      <Button variant="secondary" size="sm" onClick={() => onArchive(m)}>
                        {usingCount > 0 ? `アーカイブ (${usingCount}地区)` : 'アーカイブ'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {archived.length > 0 && (
              <tr className="bg-slate-50">
                <td colSpan={6} className="py-1.5 px-1 text-[11px] font-bold text-slate-500">
                  アーカイブ済 ({archived.length})
                </td>
              </tr>
            )}
            {archived.map((m) => (
              <tr key={m.id} className="border-b border-slate-100 align-top text-slate-400">
                <td className="py-2 pr-3">
                  <div className="font-medium">{m.displayName}</div>
                  <div className="text-[11px] font-mono">{m.id}</div>
                </td>
                <td className="py-2 pr-3 text-xs">{m.mode}</td>
                <td className="py-2 pr-3">{renderModel(m)}</td>
                <td className="py-2 pr-3">{renderUsage(m.id)}</td>
                <td className="py-2 pr-3">
                  <span className="text-xs text-slate-400">アーカイブ済</span>
                </td>
                <td className="py-2 pl-3 text-right">
                  <div className="flex gap-2 justify-end">
                    <RowExportButton collectionName="aiAnalysisSettings" item={m} />
                    <Button variant="secondary" size="sm" onClick={() => onRestore(m)}>
                      復元
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** M3 fields[] の key 必須 + 重複 (top-level + object-array 子) を検証する。 */
function validateM3Fields(fields: AiOutputFieldDef[]): string | null {
  if (fields.length === 0) return null;
  const topKeys = new Set<string>();
  for (let i = 0; i < fields.length; i += 1) {
    const f = fields[i]!;
    const key = f.key.trim();
    if (!key) return `出力フィールド 行 ${i + 1}: key は必須です`;
    if (topKeys.has(key)) return `出力フィールド 行 ${i + 1}: key "${key}" が重複しています`;
    topKeys.add(key);
    if (f.kind === 'object-array') {
      const childKeys = new Set<string>();
      for (let j = 0; j < (f.itemFields ?? []).length; j += 1) {
        const c = f.itemFields![j]!;
        const ck = c.key.trim();
        if (!ck) return `出力フィールド 行 ${i + 1} の子フィールド ${j + 1}: key は必須です`;
        if (childKeys.has(ck)) {
          return `出力フィールド 行 ${i + 1} の子フィールド ${j + 1}: key "${ck}" が重複しています`;
        }
        childKeys.add(ck);
      }
    }
  }
  return null;
}

/**
 * セクション見出しの番号を返す。引数はテンプレモードでの番号。
 *
 * 地区モードには「基本情報」節 (テンプレモードの 2) が無いため、それ以降の節は 1 つ繰り上がる。
 * 先頭の「Gemini のバージョンとパラメータ」節は両モードとも 1 で固定。
 */
function sectionNo(templateNo: number, dctx?: DistrictSectionCtx): number {
  return dctx ? templateNo - 1 : templateNo;
}

// ---------------------------------------------------------------------------
// 節 1: Gemini のバージョンとパラメータ (generationConfig)
// ---------------------------------------------------------------------------

function SectionGenerationConfig(props: {
  draft: ProfileDraft;
  setDraft: (d: ProfileDraft) => void;
  dctx?: DistrictSectionCtx;
}) {
  const { draft, setDraft, dctx } = props;
  const editor = draft.generationConfig ? (
    <GenerationConfigForm
      value={draft.generationConfig}
      onChange={(next) => setDraft({ ...draft, generationConfig: next })}
    />
  ) : (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => setDraft({ ...draft, generationConfig: { ...DEFAULT_GENERATION_CONFIG } })}
    >
      ＋ Gemini のバージョンとパラメータを設定
    </Button>
  );
  return (
    <details open className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        1. Gemini のバージョンとパラメータ (generationConfig)
      </summary>
      <div className="px-4 pb-4 grid grid-cols-1 gap-2">
        <p className="text-[11px] text-slate-500">
          最初に Gemini のバージョンを選んでください。選んだバージョンによって、この画面で設定できる
          項目 (モデル候補や入力欄) が変わります。
        </p>
        {dctx ? (
          <OverrideField
            label="Gemini のバージョンとパラメータ (generationConfig)"
            source={dctx.resolved.generationConfig.source}
            overridden={dctx.overrides.fields.generationConfig}
            onToggle={(next) => dctx.toggleField('generationConfig', next)}
            onReset={() => dctx.toggleField('generationConfig', false)}
          >
            {editor}
          </OverrideField>
        ) : (
          editor
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// 節 2: 基本情報
// ---------------------------------------------------------------------------

function SectionBasic(props: {
  draft: ProfileDraft;
  setDraft: (d: ProfileDraft) => void;
  setDisplayName: (name: string) => void;
  setMode: (mode: DistrictMode) => void;
  isNew: boolean;
  /** ドキュメント ID の手入力 (新規作成 / 複製中のみ有効)。 */
  setDocumentId: (id: string) => void;
  /** ドキュメント ID を表示名からの自動生成に戻す。 */
  onRegenerateDocumentId: () => void;
  /** 管理者が ID を手入力したか (自動生成へ戻す導線の表示条件)。 */
  idManuallyEdited: boolean;
  /** 赤字で表示する ID の問題 (null = 表示しない)。 */
  idError: string | null;
  /** ID 欄からフォーカスが外れた (未入力エラーの表示条件に使う)。 */
  onDocumentIdBlur: () => void;
}) {
  const {
    draft,
    setDisplayName,
    setMode,
    isNew,
    setDocumentId,
    onRegenerateDocumentId,
    idManuallyEdited,
    idError,
    onDocumentIdBlur,
  } = props;
  return (
    <details open className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        2. 基本情報
      </summary>
      <div className="px-4 pb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className="block text-xs text-slate-600 mb-1" htmlFor="ai-editor-name">
            表示名 <span className="text-danger-600">*</span>
          </label>
          <input
            id="ai-editor-name"
            type="text"
            value={draft.displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={FIELD_CLASS}
            placeholder="例: ECW 標準解析"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-600 mb-1" htmlFor="ai-editor-mode">
            モード (mode)
          </label>
          {/* MD-3 モードドリブン化: 選択肢は ModeProfile (M10) 登記簿から動的生成 (未登録の現在値も表示)。 */}
          <ModeSelectField
            id="ai-editor-mode"
            value={draft.mode}
            onChange={(mode) => setMode(mode)}
            className={`${FIELD_CLASS} bg-white`}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-slate-600 mb-1" htmlFor="ai-editor-doc-id">
            ドキュメント ID{' '}
            {isNew ? (
              <span className="text-danger-600">*</span>
            ) : (
              <span className="text-slate-400">(変更できません)</span>
            )}
          </label>
          <div className="flex gap-2 items-start">
            <input
              id="ai-editor-doc-id"
              type="text"
              value={draft.id}
              onChange={(e) => setDocumentId(e.target.value)}
              onBlur={onDocumentIdBlur}
              readOnly={!isNew}
              aria-invalid={idError ? true : undefined}
              className={`${FIELD_CLASS} flex-1 font-mono ${!isNew ? 'bg-slate-100 text-slate-600' : ''}`}
              placeholder="例: ecw-yokohama"
              spellCheck={false}
            />
            {isNew && idManuallyEdited && (
              <Button variant="secondary" size="sm" onClick={onRegenerateDocumentId}>
                表示名から自動生成
              </Button>
            )}
          </div>
          {isNew ? (
            <>
              {idError && <p className="mt-1 text-[11px] text-danger-600">{idError}</p>}
              <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
                半角の英小文字・数字・ハイフン (-) が使えます (例:
                ecw-yokohama)。表示名から自動で作られるので、決めたい ID
                があるときだけ書き換えてください。
                <span className="font-bold">保存すると後から変更できません</span> (地区はこの ID
                でテンプレートを参照します)。
              </p>
            </>
          ) : (
            <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">
              地区がテンプレートを参照するための ID です。作成後は変更できません。別の ID
              にしたいときは「別名で保存」で新しいテンプレートを作り、そちらに ID を付けてください。
            </p>
          )}
        </div>
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// 節 3: プロンプト (セクション分割)
// ---------------------------------------------------------------------------

function SectionPrompt(props: {
  draft: ProfileDraft;
  setDraft: (d: ProfileDraft) => void;
  onConvertLegacy: () => void;
  onDeleteLegacy: () => void;
  dctx?: DistrictSectionCtx;
}) {
  const { draft, setDraft, onConvertLegacy, onDeleteLegacy, dctx } = props;
  const hasLegacy = draft.systemInstructions.trim().length > 0;
  // 地区モード (dctx) ではレガシー本文はテンプレ(マスタ)所属のため、地区側からの
  // 変換/削除は行わない (テンプレモードでのみボタンを出す)。
  const canEditLegacy = !dctx;
  return (
    <details open className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        {sectionNo(3, dctx)}. プロンプト (セクション分割)
      </summary>
      <div className="px-4 pb-4 grid grid-cols-1 gap-3">
        <p className="text-[11px] text-slate-500">
          各欄の番号は自動アセンブル順です。「この後に自動挿入」のチップが付く欄の直後に、AI
          解析時に 投稿データ / 参照マスタ / 出力スキーマ /
          チェックリストが自動で挿入されます。ここには 「書き方の指示」のみを記述してください。
        </p>
        {hasLegacy && (
          <div className="text-[11px] bg-amber-50 border border-amber-200 rounded p-2">
            <p className="text-amber-800 font-medium">
              レガシー systemInstructions (読み取り専用) が存在します
            </p>
            <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap text-[10px] text-slate-600">
              {draft.systemInstructions}
            </pre>
            {canEditLegacy && (
              <div className="flex gap-2 mt-1 flex-wrap">
                <Button variant="secondary" size="sm" onClick={onConvertLegacy}>
                  v2 セクションへ変換
                </Button>
                <Button variant="danger" size="sm" onClick={onDeleteLegacy}>
                  レガシーを削除
                </Button>
              </div>
            )}
          </div>
        )}
        {PROMPT_SECTION_FIELDS.map((f) => {
          const body = (
            <>
              <textarea
                id={`ai-editor-ps-${f.key}`}
                value={draft.promptSections[f.key] ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    promptSections: { ...draft.promptSections, [f.key]: e.target.value },
                  })
                }
                rows={f.key === 'contentRules' ? 4 : 2}
                placeholder={f.placeholder}
                disabled={f.locked}
                className={`${FIELD_CLASS} resize-y ${f.locked ? 'bg-slate-100 text-slate-400' : ''}`}
                spellCheck={false}
              />
              {f.autoInsertAfter && (
                <span className="mt-1 inline-block px-2 py-0.5 text-[10px] bg-slate-100 text-slate-500 rounded">
                  この後に自動挿入: {f.autoInsertAfter}
                </span>
              )}
            </>
          );
          // 地区モード: 各セクションを継承トグルで包む (rules はマスタ専管 = locked)。
          if (dctx) {
            return (
              <OverrideField
                key={f.key}
                label={`${f.order}. ${f.label}`}
                source={dctx.resolved.promptSections.perSection[f.key]}
                overridden={dctx.overrides.promptSections[f.key]}
                onToggle={(next) => dctx.togglePromptSection(f.key, next)}
                onReset={() => dctx.togglePromptSection(f.key, false)}
                locked={f.locked}
              >
                {body}
              </OverrideField>
            );
          }
          return (
            <div key={f.key}>
              <label
                className="block text-xs text-slate-600 mb-1"
                htmlFor={`ai-editor-ps-${f.key}`}
              >
                <span className="inline-block px-1.5 py-0.5 mr-1 text-[10px] bg-slate-200 text-slate-600 rounded">
                  {f.order}
                </span>
                {f.label}
              </label>
              {body}
            </div>
          );
        })}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// 節 4: AI に送る投稿データ (postFields → inputFieldKeys)
// ---------------------------------------------------------------------------

function SectionInputData(props: {
  draft: ProfileDraft;
  fields: FieldDef[];
  checkedKeys: string[];
  onToggle: (key: string, checked: boolean) => void;
  onAll: () => void;
  onNone: () => void;
  dctx?: DistrictSectionCtx;
}) {
  const { draft, fields, checkedKeys, onToggle, onAll, onNone, dctx } = props;
  const checkedSet = new Set(checkedKeys);
  const stateLabel =
    draft.inputFieldKeys === undefined
      ? '全選択（既定: 入力項目をすべて送る）'
      : draft.inputFieldKeys.length === 0
        ? '全解除 (何も送らない)'
        : `${draft.inputFieldKeys.length} 件選択`;
  const editor = (
    <>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={onAll}>
          全選択 (既定)
        </Button>
        <Button variant="secondary" size="sm" onClick={onNone}>
          全解除
        </Button>
      </div>
      {fields.length === 0 ? (
        <p className="text-xs text-slate-400">送れる入力項目がありません。</p>
      ) : (
        <div
          role="group"
          aria-label="AI に送る投稿データ選択 (inputFieldKeys)"
          className="grid grid-cols-1 gap-1 max-h-56 overflow-auto border border-slate-200 rounded p-2"
        >
          {fields.map((f) => (
            <label key={f.key} className="inline-flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={checkedSet.has(f.key)}
                onChange={(e) => onToggle(f.key, e.target.checked)}
                aria-label={`${f.label} (${f.key}) を AI に送る`}
              />
              <span>
                <strong>{f.label}</strong>
                <span className="ml-1 text-slate-400">
                  key: {f.key} · 型: {f.type}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </>
  );
  return (
    <details open className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        {sectionNo(4, dctx)}. AI に送る投稿データ ({stateLabel})
      </summary>
      <div className="px-4 pb-4 grid grid-cols-1 gap-2">
        <p className="text-[11px] text-slate-500">
          {dctx
            ? 'この地区の投稿時の入力項目（非表示を除く）から、AI 解析・タイトル生成に送る項目を選びます。既定（全選択）では全項目を送り、地区で項目を追加したときも自動で送ります。'
            : 'モードの入力項目から、AI 解析・タイトル生成に送る項目を選びます。地区で使うときは、既定（全選択）なら地区の入力項目（非表示を除く）がすべて送られます。'}
        </p>
        {dctx ? (
          <OverrideField
            label="送信フィールド (inputFieldKeys)"
            source={dctx.resolved.inputFieldKeys.source}
            overridden={dctx.overrides.fields.inputFieldKeys}
            onToggle={(next) => dctx.toggleField('inputFieldKeys', next)}
            onReset={() => dctx.toggleField('inputFieldKeys', false)}
          >
            {editor}
          </OverrideField>
        ) : (
          editor
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// 節 5: 参照カテゴリマスタ (M2)
// ---------------------------------------------------------------------------

function SectionCategoryMasters(props: {
  draft: ProfileDraft;
  m2Items: (AiCategoryMaster & { id: string })[];
  onToggle: (id: string, checked: boolean) => void;
  dctx?: DistrictSectionCtx;
}) {
  const { draft, m2Items, onToggle, dctx } = props;
  const sameModeM2s = m2Items.filter((m) => categoryMasterMatchesMode(m, draft.mode));
  const editor = (
    <>
      <div
        role="group"
        aria-label="参照カテゴリマスタ選択 (categoryMasterIds)"
        className="grid grid-cols-1 gap-1 max-h-40 overflow-auto border border-slate-200 rounded p-2"
      >
        {sameModeM2s.length === 0 ? (
          <p className="text-xs text-slate-400">この mode の M2 マスタはまだありません。</p>
        ) : (
          sameModeM2s.map((m2) => (
            <label key={m2.id} className="inline-flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={draft.categoryMasterIds.includes(m2.id)}
                onChange={(e) => onToggle(m2.id, e.target.checked)}
              />
              <span>
                <strong>{m2.displayName}</strong>
                <span className="ml-1 text-slate-400">
                  id: {m2.id} · templateVar: {m2.templateVar || '(無)'} · rows: {m2.rows.length}
                </span>
              </span>
            </label>
          ))
        )}
      </div>
      {draft.categoryMasterIds.length > 0 && (
        <div className="grid grid-cols-1 gap-2">
          {draft.categoryMasterIds.map((id) => {
            const m2 = m2Items.find((x) => x.id === id);
            if (!m2) return null;
            return (
              <details key={id} className="text-xs border border-slate-200 rounded p-2">
                <summary className="cursor-pointer text-slate-700">
                  <strong>{m2.displayName}</strong>
                  <span className="ml-2 text-slate-400">
                    templateVar: <code>{`{{${m2.templateVar || '?'}}}`}</code> · {m2.rows.length} 行
                  </span>
                </summary>
                <div className="mt-2 grid grid-cols-1 gap-0.5 text-[11px] text-slate-600">
                  {m2.rows.slice(0, 8).map((r, i) => (
                    <div key={`${id}-${i}`}>
                      {r.major
                        ? `${r.majorCd ?? ''}-${r.categoryCd}: ${r.major}/${r.category}`
                        : `${r.categoryCd}: ${r.category}`}
                    </div>
                  ))}
                  {m2.rows.length > 8 && (
                    <div className="text-slate-400">... 他 {m2.rows.length - 8} 行</div>
                  )}
                </div>
                <Link
                  to="/masters/ai-category"
                  className="mt-1 inline-block text-[10px] text-primary-600 hover:underline"
                >
                  → テーブル編集 (M2 ページ)
                </Link>
              </details>
            );
          })}
        </div>
      )}
    </>
  );
  return (
    <details open className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        {sectionNo(5, dctx)}. 参照カテゴリマスタ (M2、{draft.categoryMasterIds.length} 件)
      </summary>
      <div className="px-4 pb-4 grid grid-cols-1 gap-2">
        <p className="text-[11px] text-slate-500">
          AI に渡す選択肢の語彙 (M2) を複数選択します。行内容は折りたたみで確認でき、行編集は M2
          専用 ページで行います。
        </p>
        {dctx ? (
          <OverrideField
            label="参照カテゴリマスタ (categoryMasterIds、M2)"
            source={dctx.resolved.categoryMasterIds.source}
            overridden={dctx.overrides.fields.categoryMasterIds}
            onToggle={(next) => dctx.toggleField('categoryMasterIds', next)}
            onReset={() => dctx.toggleField('categoryMasterIds', false)}
          >
            {editor}
          </OverrideField>
        ) : (
          editor
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// 節 6: 出力フォーマット (M3 select + inline schema 編集)
// ---------------------------------------------------------------------------

function SectionOutput(props: {
  draft: ProfileDraft;
  m3Items: (AiOutputFormat & { id: string })[];
  m3Draft: M3Draft | null;
  setM3Draft: (d: M3Draft | null) => void;
  onSelect: (id: string) => void;
  dctx?: DistrictSectionCtx;
}) {
  const { draft, m3Items, m3Draft, setM3Draft, onSelect, dctx } = props;
  // テンプレモードは出力フォーマットを自モードと同一に限定する (テンプレ＝全地区に影響するため)。
  // 地区モード (dctx) では「プロンプト/入力はこのモードだが出力形式だけ別モード」という
  // mode 横断の組み合わせを許す (本番 箱根景観調査の要望)。一致 mode を上位に並べて全件出す。
  const candidates = dctx
    ? [...m3Items].sort((a, b) => {
        const am = a.mode === draft.mode ? 0 : 1;
        const bm = b.mode === draft.mode ? 0 : 1;
        if (am !== bm) return am - bm;
        return a.displayName.localeCompare(b.displayName);
      })
    : m3Items.filter((m) => m.mode === draft.mode);

  function update(patch: Partial<M3Draft>) {
    if (!m3Draft) return;
    setM3Draft({ ...m3Draft, ...patch });
  }

  // ---- classificationAxes ハンドラ ----
  function addAxis() {
    if (!m3Draft) return;
    update({
      classificationAxes: [
        ...m3Draft.classificationAxes,
        { codeField: '', nameField: '', subField: '', label: '', definition: '', role: '' },
      ],
    });
  }
  function updateAxis(i: number, patch: Partial<M3AxisDraft>) {
    if (!m3Draft) return;
    const next = [...m3Draft.classificationAxes];
    if (!next[i]) return;
    next[i] = { ...next[i], ...patch };
    update({ classificationAxes: next });
  }
  function removeAxis(i: number) {
    if (!m3Draft) return;
    update({ classificationAxes: m3Draft.classificationAxes.filter((_, idx) => idx !== i) });
  }

  // ---- levels ハンドラ ----
  function addLevel() {
    if (!m3Draft) return;
    update({ assessmentLevels: [...m3Draft.assessmentLevels, ''] });
  }
  function updateLevel(i: number, value: string) {
    if (!m3Draft) return;
    const next = [...m3Draft.assessmentLevels];
    if (next[i] === undefined) return;
    next[i] = value;
    update({ assessmentLevels: next });
  }
  function removeLevel(i: number) {
    if (!m3Draft) return;
    update({ assessmentLevels: m3Draft.assessmentLevels.filter((_, idx) => idx !== i) });
  }

  // ---- fields[] ハンドラ ----
  const [newFieldKind, setNewFieldKind] = useState<OutputFieldKind>('text');
  function setFields(fields: AiOutputFieldDef[]) {
    if (!m3Draft) return;
    update({ fields });
  }
  function addField() {
    if (!m3Draft) return;
    setFields([...m3Draft.fields, emptyFieldOfKind(newFieldKind)]);
  }
  function updateField(i: number, next: AiOutputFieldDef) {
    if (!m3Draft) return;
    const arr = [...m3Draft.fields];
    if (!arr[i]) return;
    arr[i] = next;
    setFields(arr);
  }
  function removeField(i: number) {
    if (!m3Draft) return;
    setFields(m3Draft.fields.filter((_, idx) => idx !== i));
  }
  function moveField(i: number, dir: -1 | 1) {
    if (!m3Draft) return;
    const t = i + dir;
    if (t < 0 || t >= m3Draft.fields.length) return;
    const arr = [...m3Draft.fields];
    const tmp = arr[i]!;
    arr[i] = arr[t]!;
    arr[t] = tmp;
    setFields(arr);
  }
  function generateFieldsFromStructured() {
    if (!m3Draft) return;
    const tmpAxes: AiClassificationAxis[] = m3Draft.classificationAxes
      .filter((a) => a.codeField.trim() && a.nameField.trim())
      .map((a) => {
        const axis: AiClassificationAxis = {
          codeField: a.codeField.trim(),
          nameField: a.nameField.trim(),
          label: a.label.trim() || a.codeField.trim(),
        };
        const sub = a.subField.trim();
        if (sub) axis.subField = sub;
        if (a.role === 'primary' || a.role === 'secondary') axis.role = a.role;
        return axis;
      });
    const tmpSchema: AiOutputFormatSchema = {
      classificationAxes: tmpAxes,
      assessment: {
        positiveLabel: m3Draft.assessmentPositiveLabel.trim(),
        negativeLabel: m3Draft.assessmentNegativeLabel.trim(),
        levels: m3Draft.assessmentLevels.map((l) => l.trim()).filter((l) => l.length > 0),
      },
      aggregateScores: {},
      reasoningField: m3Draft.reasoningField.trim(),
    };
    const normalized = normalizeAiOutputFormat(tmpSchema);
    setFields((normalized.fields ?? []).map(cloneField));
  }
  function addChildField(fieldIndex: number, kind: ChildFieldKind) {
    if (!m3Draft) return;
    const f = m3Draft.fields[fieldIndex];
    if (!f || f.kind !== 'object-array') return;
    const next: AiOutputObjectArrayFieldDef = {
      ...f,
      itemFields: [...(f.itemFields ?? []), emptyChildField(kind)],
    };
    updateField(fieldIndex, next);
  }
  function updateChildField(fieldIndex: number, childIndex: number, child: AiOutputFieldDef) {
    if (!m3Draft) return;
    const f = m3Draft.fields[fieldIndex];
    if (!f || f.kind !== 'object-array') return;
    const children = [...(f.itemFields ?? [])];
    if (!children[childIndex]) return;
    children[childIndex] = child;
    updateField(fieldIndex, { ...f, itemFields: children });
  }
  function removeChildField(fieldIndex: number, childIndex: number) {
    if (!m3Draft) return;
    const f = m3Draft.fields[fieldIndex];
    if (!f || f.kind !== 'object-array') return;
    updateField(fieldIndex, {
      ...f,
      itemFields: (f.itemFields ?? []).filter((_, idx) => idx !== childIndex),
    });
  }

  // 出力する項目 (fields[]) のエディタ。テンプレモードでは出力フォーマット全体の一部として、
  // 地区モードでは「この地区だけの出力項目」上書きの編集対象として共用する。地区モードでは
  // 元フォーマットの構造そのもの (ルートキー/分類軸/評価ラベル/集約/根拠) は一切出さない
  // (テンプレ＝全地区に影響するため、地区が触れてよいのは出力項目の差し替えだけ)。
  const fieldsEditor = m3Draft ? (
    <div className="border-t border-slate-200 pt-2">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <span className="text-xs font-bold text-slate-700">
          出力する項目（結果に並ぶ項目、{m3Draft.fields.length} 件）
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={newFieldKind}
            onChange={(e) => setNewFieldKind(e.target.value as OutputFieldKind)}
            className={INPUT_CLASS}
            style={{ width: 'auto', maxWidth: '100%' }}
            aria-label="追加する項目の種類"
          >
            <option value="text">文章</option>
            <option value="enum-array">候補から選ぶ（複数可）</option>
            <option value="code-list-string">コードの列</option>
            <option value="object-array">くり返しの表</option>
          </select>
          <Button variant="secondary" size="sm" onClick={addField}>
            ＋ 項目を追加
          </Button>
          <Button variant="secondary" size="sm" onClick={generateFieldsFromStructured}>
            標準の項目を作る
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => setFields([])}
            disabled={m3Draft.fields.length === 0}
          >
            すべて消す
          </Button>
        </div>
      </div>
      {m3Draft.fields.length > 0 && (
        <p className="text-[11px] text-primary-700 bg-primary-50 border border-primary-200 rounded px-2 py-1 mb-2">
          出力する項目を個別に指定しています（自動生成より優先されます）。
        </p>
      )}
      {m3Draft.fields.length === 0 ? (
        <p className="text-[11px] text-slate-500">
          出力する項目はまだ指定されていません（未指定なら標準の項目が自動で使われます）。
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {m3Draft.fields.map((field, i) => (
            <FieldEditor
              key={i}
              index={i}
              total={m3Draft.fields.length}
              field={field}
              onChange={(next) => updateField(i, next)}
              onRemove={() => removeField(i)}
              onMove={(dir) => moveField(i, dir)}
              onAddChild={(kind) => addChildField(i, kind)}
              onUpdateChild={(ci, child) => updateChildField(i, ci, child)}
              onRemoveChild={(ci) => removeChildField(i, ci)}
            />
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <details open className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        {sectionNo(6, dctx)}. 出力フォーマット（結果のかたち）
      </summary>
      <div className="px-4 pb-4 grid grid-cols-1 gap-3">
        {(() => {
          const selectEl = (
            <select
              id="ai-editor-m3"
              value={draft.outputFormatId}
              onChange={(e) => onSelect(e.target.value)}
              className={`${FIELD_CLASS} bg-white`}
            >
              <option value="">(選択してください)</option>
              {candidates.map((m3) => (
                <option key={m3.id} value={m3.id}>
                  {m3.displayName} ({m3.id})
                  {dctx && m3.mode !== draft.mode ? `（${getModeLabel(m3.mode)}）` : ''}
                </option>
              ))}
            </select>
          );
          if (dctx) {
            return (
              <OverrideField
                label="この地区が使う出力フォーマット（結果のかたち）"
                source={dctx.resolved.outputFormatId.source}
                overridden={dctx.overrides.fields.outputFormatId}
                onToggle={(next) => dctx.toggleField('outputFormatId', next)}
                onReset={() => dctx.toggleField('outputFormatId', false)}
              >
                {selectEl}
              </OverrideField>
            );
          }
          return (
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="ai-editor-m3">
                出力フォーマット <span className="text-danger-600">*</span>
              </label>
              {selectEl}
            </div>
          );
        })()}

        {dctx ? (
          /* 地区モード: 出力フォーマットはマスタ (全地区共通) の資産。地区が変えられるのは
             「この地区だけの出力項目の差し替え」(outputFieldsOverride) だけ。元フォーマットの構造
             = ルートキー/分類軸/評価ラベル/集約/根拠 はここでは一切編集させない (テンプレを直接
             書き換えると全地区に波及するため)。テンプレ自体の編集は出力フォーマット専用ページで行う。 */
          <OverrideField
            label="この地区だけの出力項目（結果に並ぶ項目）"
            source={dctx.resolved.outputFieldsOverride.source}
            overridden={dctx.overrides.fields.outputFields}
            onToggle={(next) => dctx.toggleField('outputFields', next)}
            onReset={() => dctx.toggleField('outputFields', false)}
          >
            <p className="text-[11px] text-slate-500 mb-2">
              この地区だけ、AI が出力する項目（結果に並ぶ項目）を差し替えます。元のフォーマット
              （テンプレート）は変更されず、ほかの地区にも影響しません。チェックを外すと
              テンプレートの項目をそのまま使います。
            </p>
            {m3Draft ? (
              fieldsEditor
            ) : (
              <p className="text-[11px] text-slate-500">
                先に上で出力フォーマットを選んでください。
              </p>
            )}
          </OverrideField>
        ) : (
          m3Draft && (
            <div className="grid grid-cols-1 gap-3 border border-slate-200 rounded-md p-3 bg-slate-50/50">
              <p className="text-[11px] text-primary-700">
                選択中のフォーマット「{m3Draft.displayName}」を直接編集します。これは
                テンプレート本体の編集で、このフォーマットを使う<strong>全地区</strong>に影響します
                （保存時に反映）。
              </p>

              {/* rootKey */}
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="ai-editor-m3-rootkey">
                  ルートキー (rootKey、任意)
                </label>
                <input
                  id="ai-editor-m3-rootkey"
                  type="text"
                  value={m3Draft.rootKey}
                  onChange={(e) => update({ rootKey: e.target.value })}
                  className={FIELD_CLASS}
                  placeholder="例: ecw_w (空欄ならトップレベル)"
                />
              </div>

              {/* classificationAxes */}
              <div className="border-t border-slate-200 pt-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-slate-700">
                    分類軸 (classificationAxes、{m3Draft.classificationAxes.length} 件)
                  </span>
                  <Button variant="secondary" size="sm" onClick={addAxis}>
                    ＋ 軸を追加
                  </Button>
                </div>
                {m3Draft.classificationAxes.length === 0 ? (
                  <p className="text-[11px] text-slate-500">軸がありません。</p>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {m3Draft.classificationAxes.map((axis, i) => (
                      <div
                        key={i}
                        className="border border-slate-200 rounded p-2 bg-white grid grid-cols-1 gap-1"
                      >
                        <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                          <input
                            type="text"
                            value={axis.codeField}
                            onChange={(e) => updateAxis(i, { codeField: e.target.value })}
                            className={INPUT_CLASS}
                            placeholder="codeField (例: category_code)"
                            aria-label={`軸 ${i + 1} codeField`}
                          />
                          <input
                            type="text"
                            value={axis.nameField}
                            onChange={(e) => updateAxis(i, { nameField: e.target.value })}
                            className={INPUT_CLASS}
                            placeholder="nameField (例: category_name)"
                            aria-label={`軸 ${i + 1} nameField`}
                          />
                          <input
                            type="text"
                            value={axis.subField}
                            onChange={(e) => updateAxis(i, { subField: e.target.value })}
                            className={INPUT_CLASS}
                            placeholder="subField (任意)"
                            aria-label={`軸 ${i + 1} subField`}
                          />
                          <input
                            type="text"
                            value={axis.label}
                            onChange={(e) => updateAxis(i, { label: e.target.value })}
                            className={INPUT_CLASS}
                            placeholder="label (例: カテゴリ)"
                            aria-label={`軸 ${i + 1} label`}
                          />
                        </div>
                        <textarea
                          value={axis.definition}
                          onChange={(e) => updateAxis(i, { definition: e.target.value })}
                          className={`${INPUT_CLASS} resize-y`}
                          rows={2}
                          placeholder="軸の定義 (definition、任意・プロンプトに注入)"
                          aria-label={`軸 ${i + 1} definition`}
                        />
                        <div className="flex items-center gap-2">
                          <select
                            value={axis.role}
                            onChange={(e) =>
                              updateAxis(i, { role: e.target.value as M3AxisDraft['role'] })
                            }
                            className={INPUT_CLASS}
                            style={{ width: 'auto', maxWidth: '100%' }}
                            aria-label={`軸 ${i + 1} role`}
                          >
                            <option value="">(未指定)</option>
                            <option value="primary">primary</option>
                            <option value="secondary">secondary</option>
                          </select>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeAxis(i)}
                            aria-label={`軸 ${i + 1} 削除`}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* assessment */}
              <div className="border-t border-slate-200 pt-2 grid grid-cols-1 gap-2">
                <span className="text-xs font-bold text-slate-700">評価ラベル (assessment)</span>
                <div className="grid grid-cols-1 gap-1 md:grid-cols-2">
                  <input
                    type="text"
                    value={m3Draft.assessmentPositiveLabel}
                    onChange={(e) => update({ assessmentPositiveLabel: e.target.value })}
                    className={INPUT_CLASS}
                    placeholder="positiveLabel (例: ホッと)"
                    aria-label="assessment positiveLabel"
                  />
                  <input
                    type="text"
                    value={m3Draft.assessmentNegativeLabel}
                    onChange={(e) => update({ assessmentNegativeLabel: e.target.value })}
                    className={INPUT_CLASS}
                    placeholder="negativeLabel (例: ハッと)"
                    aria-label="assessment negativeLabel"
                  />
                </div>
                <textarea
                  value={m3Draft.assessmentDefinition}
                  onChange={(e) => update({ assessmentDefinition: e.target.value })}
                  className={`${INPUT_CLASS} resize-y`}
                  rows={2}
                  placeholder="評価の定義 (definition、任意)"
                  aria-label="assessment definition"
                />
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-600">
                    評価レベル (levels、{m3Draft.assessmentLevels.length} 件)
                  </span>
                  <Button variant="secondary" size="sm" onClick={addLevel}>
                    ＋ level 追加
                  </Button>
                </div>
                {m3Draft.assessmentLevels.map((level, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={level}
                      onChange={(e) => updateLevel(i, e.target.value)}
                      className={`${INPUT_CLASS} flex-1`}
                      placeholder={`level ${i + 1}`}
                      aria-label={`level ${i + 1}`}
                    />
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => removeLevel(i)}
                      aria-label={`level ${i + 1} 削除`}
                    >
                      ✕
                    </Button>
                  </div>
                ))}
              </div>

              {/* aggregateScores */}
              <div className="border-t border-slate-200 pt-2 grid grid-cols-1 gap-2">
                <span className="text-xs font-bold text-slate-700">
                  集約スコアキー (aggregateScores)
                </span>
                <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
                  <input
                    type="text"
                    value={m3Draft.aggregatePositiveKey}
                    onChange={(e) => update({ aggregatePositiveKey: e.target.value })}
                    className={INPUT_CLASS}
                    placeholder="positiveKey (例: hotto_score)"
                    aria-label="aggregate positiveKey"
                  />
                  <input
                    type="text"
                    value={m3Draft.aggregateNegativeKey}
                    onChange={(e) => update({ aggregateNegativeKey: e.target.value })}
                    className={INPUT_CLASS}
                    placeholder="negativeKey (例: hatto_score)"
                    aria-label="aggregate negativeKey"
                  />
                  <input
                    type="text"
                    value={m3Draft.aggregateOverallKey}
                    onChange={(e) => update({ aggregateOverallKey: e.target.value })}
                    className={INPUT_CLASS}
                    placeholder="overallKey (例: walkable_score)"
                    aria-label="aggregate overallKey"
                  />
                </div>
                <textarea
                  value={m3Draft.aggregateDefinition}
                  onChange={(e) => update({ aggregateDefinition: e.target.value })}
                  className={`${INPUT_CLASS} resize-y`}
                  rows={2}
                  placeholder="集約スコアの定義 (definition、任意)"
                  aria-label="aggregate definition"
                />
              </div>

              {/* reasoningField */}
              <div className="border-t border-slate-200 pt-2">
                <label
                  className="block text-xs text-slate-600 mb-1"
                  htmlFor="ai-editor-m3-reasoning"
                >
                  根拠フィールド名 (reasoningField)
                </label>
                <input
                  id="ai-editor-m3-reasoning"
                  type="text"
                  value={m3Draft.reasoningField}
                  onChange={(e) => update({ reasoningField: e.target.value })}
                  className={FIELD_CLASS}
                  placeholder="例: reasoning"
                />
              </div>

              {fieldsEditor}

              <Link
                to="/masters/ai-output-format"
                className="text-[11px] text-primary-600 hover:underline"
              >
                → 出力フォーマット専用ページで編集（新規作成・アーカイブ等）
              </Link>
            </div>
          )
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// 節 7: 出力検証 (validators)
// ---------------------------------------------------------------------------

function SectionValidators(props: {
  draft: ProfileDraft;
  setDraft: (d: ProfileDraft) => void;
  m2Items: (AiCategoryMaster & { id: string })[];
  selectedM3: (AiOutputFormat & { id: string }) | undefined;
  dctx?: DistrictSectionCtx;
}) {
  const { draft, setDraft, m2Items, selectedM3, dctx } = props;
  const availableCategoryMasters = m2Items.filter((m) => draft.categoryMasterIds.includes(m.id));
  const editor = (
    <ValidatorRulesForm
      value={draft.validatorRules}
      onChange={(next) => setDraft({ ...draft, validatorRules: next })}
      mode={draft.mode}
      outputFormat={selectedM3}
      availableCategoryMasters={availableCategoryMasters}
    />
  );
  return (
    <details className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        {sectionNo(7, dctx)}. 出力検証 (validators、{draft.validatorRules.length} 件)
      </summary>
      <div className="px-4 pb-4">
        {dctx ? (
          <OverrideField
            label="出力検証ルール (validators)"
            source={dctx.resolved.validators.source}
            overridden={dctx.overrides.fields.validators}
            onToggle={(next) => dctx.toggleField('validators', next)}
            onReset={() => dctx.toggleField('validators', false)}
          >
            {editor}
          </OverrideField>
        ) : (
          editor
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// 節 8: タイトル自動生成 (titleGeneration)
// ---------------------------------------------------------------------------

function SectionTitleGeneration(props: {
  draft: ProfileDraft;
  setDraft: (d: ProfileDraft) => void;
  dctx?: DistrictSectionCtx;
}) {
  const { draft, setDraft, dctx } = props;
  const editor = (
    <TitleGenerationForm
      value={draft.titleGeneration}
      onChange={(next) => setDraft({ ...draft, titleGeneration: next })}
    />
  );
  return (
    <details className="border border-slate-200 rounded-md bg-white">
      <summary className="cursor-pointer px-4 py-3 text-sm font-bold text-slate-700">
        {sectionNo(8, dctx)}. タイトル自動生成 (titleGeneration)
      </summary>
      <div className="px-4 pb-4">
        {dctx ? (
          <OverrideField
            label="タイトル自動生成 (titleGeneration)"
            source={dctx.resolved.titleGeneration.source}
            overridden={dctx.overrides.fields.titleGeneration}
            onToggle={(next) => dctx.toggleField('titleGeneration', next)}
            onReset={() => dctx.toggleField('titleGeneration', false)}
          >
            {editor}
          </OverrideField>
        ) : (
          editor
        )}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// RIGHT — sticky preview pane
// ---------------------------------------------------------------------------

function PreviewWarnings(props: { warnings: IntegrityWarning[]; mode: DistrictMode }) {
  const { warnings, mode } = props;
  if (warnings.length === 0) {
    return (
      <Card padding="md" className="bg-green-50 border-green-200">
        <h3 className="text-xs font-bold text-green-700">整合性チェック (mode={mode})</h3>
        <p className="text-xs text-green-700 mt-1">✓ 警告はありません</p>
      </Card>
    );
  }
  const errors = warnings.filter((w) => w.level === 'error');
  const warns = warnings.filter((w) => w.level === 'warn');
  const infos = warnings.filter((w) => w.level === 'info');
  return (
    <Card padding="md">
      <h3 className="text-xs font-bold text-slate-700 mb-2">
        整合性チェック (mode={mode}、{warnings.length} 件)
      </h3>
      <ul className="grid grid-cols-1 gap-1.5 text-xs">
        {errors.map((w, i) => (
          <li key={`e${i}`} className="text-danger-700">
            🔴 {w.message}
            {w.hint && <span className="block text-[10px] text-slate-500 ml-4">→ {w.hint}</span>}
          </li>
        ))}
        {warns.map((w, i) => (
          <li key={`w${i}`} className="text-amber-700">
            ⚠ {w.message}
            {w.hint && <span className="block text-[10px] text-slate-500 ml-4">→ {w.hint}</span>}
          </li>
        ))}
        {infos.map((w, i) => (
          <li key={`i${i}`} className="text-slate-600">
            ℹ {w.message}
            {w.hint && <span className="block text-[10px] text-slate-500 ml-4">→ {w.hint}</span>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

const PREVIEW_PRE_CLASS =
  'text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 max-h-[50vh] overflow-auto whitespace-pre-wrap';

/**
 * AI 送信ペイロードのライブプレビュー (方式A)。
 * バックエンドと同一の buildPrompt / formatOutputSchema で「実際に送るデータそのもの」を
 * 全セクション (プロンプト全文 / 投稿データ / 出力フォーマット / 出力検証 / タイトル生成 /
 * Gemini パラメータ) について表示する。全編集項目に動的追従する。
 */
function PreviewPayload(props: { preview: AiPayloadPreview | null }) {
  const p = props.preview;
  if (!p) return null;
  return (
    <>
      <Card padding="md">
        <h3 className="text-xs font-bold text-slate-700 mb-2">
          ① システムプロンプト (実際に送る全文・live)
        </h3>
        <pre className={PREVIEW_PRE_CLASS}>{p.systemPrompt || '(まだ設定が選ばれていません)'}</pre>
      </Card>

      <Card padding="md">
        <h3 className="text-xs font-bold text-slate-700 mb-2">② AI に送る投稿データ (live)</h3>
        {p.postData.length === 0 ? (
          <p className="text-[11px] text-slate-500">送信する投稿項目がありません。</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="pr-2 pb-1">変数</th>
                  <th className="pr-2 pb-1">投稿項目</th>
                  <th className="pr-2 pb-1">日本語名</th>
                  <th className="pb-1">送信</th>
                </tr>
              </thead>
              <tbody>
                {p.postData.map((r) => (
                  <tr key={r.templateVar} className={r.sent ? '' : 'opacity-40'}>
                    <td className="pr-2 font-mono">{r.templateVar}</td>
                    <td className="pr-2 font-mono text-slate-500">{r.source}</td>
                    <td className="pr-2">{r.label || '—'}</td>
                    <td>{r.sent ? '✓' : '×'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[10px] text-slate-400 mt-2">{p.imageNote}</p>
      </Card>

      <Card padding="md">
        <h3 className="text-xs font-bold text-slate-700 mb-2">
          ③ 出力フォーマット (AI が返す JSON・live)
        </h3>
        <pre className={PREVIEW_PRE_CLASS}>{p.outputSchema}</pre>
      </Card>

      <Card padding="md">
        <h3 className="text-xs font-bold text-slate-700 mb-2">
          ④ 出力検証ルール ({p.validators.length})
        </h3>
        {p.validators.length === 0 ? (
          <p className="text-[11px] text-slate-500">検証ルールなし。</p>
        ) : (
          <ul className="text-[11px] text-slate-700 grid grid-cols-1 gap-1">
            {p.validators.map((v, i) => (
              <li key={i}>・{v}</li>
            ))}
          </ul>
        )}
      </Card>

      <Card padding="md">
        <h3 className="text-xs font-bold text-slate-700 mb-2">⑤ タイトル自動生成</h3>
        {p.titleGeneration.enabled ? (
          <div className="text-[11px] text-slate-700">
            <p>有効 (投稿後に別途 AI でタイトルを生成します)</p>
            {p.titleGeneration.instructions && (
              <pre className={`${PREVIEW_PRE_CLASS} mt-1`}>{p.titleGeneration.instructions}</pre>
            )}
          </div>
        ) : (
          <p className="text-[11px] text-slate-500">無効。</p>
        )}
      </Card>

      <Card padding="md">
        <h3 className="text-xs font-bold text-slate-700 mb-2">⑥ Gemini パラメータ</h3>
        <div className="overflow-x-auto">
          <table className="text-[11px]">
            <tbody>
              {p.generationParams.map((g) => (
                <tr key={g.label}>
                  <td className="pr-3 text-slate-500 align-top">{g.label}</td>
                  <td className="font-mono">
                    {g.value}
                    {g.isDefault && <span className="text-[10px] text-slate-400 ml-1">(既定)</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

// ===========================================================================================
// Phase 4: 地区モード (DistrictAiEditor)
// -------------------------------------------------------------------------------------------
// district doc を読み、aiAnalysis.settingsId で参照テンプレ (M1) を解決し、
// resolveEffectiveAiAnalysis(m1, district.aiAnalysis) で実効値 + 由来を得る。draft は実効値で初期化、
// 各項目に override ON/OFF トグル (DistrictOverrideState) を持つ。継承中はグレー + disabled、ON で
// 通常色編集可。保存は ON 項目だけ詰めた DistrictAiAnalysisConfig を setDistrictAiAnalysis で全置換。
// レイアウト = テンプレモードと同一の縦アコーディオン + 右 sticky プレビュー。
// ===========================================================================================

/**
 * docs/29 §7-1: 地区モードの観点タブ。地区の解析の観点 (既定 + 追加) をタブで切り替え、観点ごとに本体を作り直す
 * (観点が変わると draft は作り直し。保存していない編集があれば切り替える前に確認する)。
 */
function DistrictAiEditor(props: { districtId: string; uid?: string }) {
  const districtState = useDistrict(props.districtId);
  const [analysisKey, setAnalysisKey] = useState<string>(DEFAULT_POST_ANALYSIS_KEY);
  const dirtyRef = useRef(false);
  const onDirtyChange = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);
  const options =
    districtState.status === 'ready'
      ? listPostAnalysisOptions(districtState.district.aiAnalysis)
      : [];
  const activeKey = options.some((o) => o.key === analysisKey)
    ? analysisKey
    : DEFAULT_POST_ANALYSIS_KEY;
  const tabs =
    options.length > 1 ? (
      <div
        role="tablist"
        aria-label="解析の観点"
        className="mt-3 flex flex-wrap gap-1 border-b border-slate-200"
      >
        {options.map((o) => {
          const active = o.key === activeKey;
          return (
            <button
              key={o.key}
              type="button"
              role="tab"
              aria-selected={active}
              className={`px-3 py-1.5 -mb-px text-sm font-medium border-b-2 ${active ? 'border-primary-600 text-primary-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => {
                if (active) return;
                if (
                  dirtyRef.current &&
                  !confirm('保存していない変更があります。破棄して観点を切り替えますか？')
                ) {
                  return;
                }
                dirtyRef.current = false;
                setAnalysisKey(o.key);
              }}
            >
              {o.label}
              {o.isDefault ? '（既定）' : ''}
            </button>
          );
        })}
      </div>
    ) : null;
  return (
    <DistrictAiEditorBody
      key={activeKey}
      districtId={props.districtId}
      uid={props.uid}
      analysisKey={activeKey}
      analysisLabel={options.find((o) => o.key === activeKey)?.label ?? null}
      tabs={tabs}
      onDirtyChange={onDirtyChange}
    />
  );
}

function DistrictAiEditorBody(props: {
  districtId: string;
  uid?: string;
  /** docs/29: 編集する観点 ('default' = 既定の観点 = district.aiAnalysis 直下)。 */
  analysisKey: string;
  analysisLabel: string | null;
  tabs: ReactNode;
  /** 保存していない変更があるかを親へ知らせる (観点を切り替える前の確認に使う)。 */
  onDirtyChange: (dirty: boolean) => void;
}) {
  const uid = props.uid?.trim() || 'admin-stub';
  const { onDirtyChange } = props;
  const isDefaultAnalysis = isDefaultPostAnalysis(props.analysisKey);
  const { toast } = useToast();
  const districtState = useDistrict(props.districtId);
  const m1State = useAiAnalysisSettings();
  const m2State = useAiCategoryMasters();
  const m3State = useAiOutputFormats();

  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [overrides, setOverrides] = useState<DistrictOverrideState>(emptyOverrideState());
  /** M3 fields[] override 編集 draft (outputFields トグル ON のとき消費)。 */
  const [m3Draft, setM3Draft] = useState<M3Draft | null>(null);
  const [saving, setSaving] = useState(false);
  /** 既に初期化済みか (district / m1 が ready になった初回のみ draft を構築する)。 */
  const [initialized, setInitialized] = useState(false);
  /** 初期化・保存の直後の内容 (保存していない変更の判定用)。 */
  const dirtySnapshot = useRef<string | null>(null);

  const district = districtState.status === 'ready' ? districtState.district : null;
  // docs/29 §7-1: 観点の地区の設定 (既定 = district.aiAnalysis、追加 = 観点の解析設定 + 観点の上書き)。
  const analysisConfig = useMemo(
    () => resolvePostAnalysisAiConfig(district?.aiAnalysis ?? null, props.analysisKey),
    [district, props.analysisKey],
  );

  const m1Items = useMemo(() => (m1State.status === 'ready' ? m1State.items : []), [m1State]);
  const m2Items = useMemo(() => (m2State.status === 'ready' ? m2State.items : []), [m2State]);
  const m3Items = useMemo(() => (m3State.status === 'ready' ? m3State.items : []), [m3State]);

  // 参照テンプレ (M1): district.aiAnalysis.settingsId で解決。
  const templateM1 = useMemo(() => {
    const settingsId = analysisConfig?.settingsId;
    if (!settingsId) return undefined;
    return m1Items.find((m) => m.id === settingsId);
  }, [analysisConfig, m1Items]);

  // 実効値 + 由来 (resolveEffectiveAiAnalysis を使用、独自マージ禁止)。
  const resolved: ResolvedAiAnalysis | null = useMemo(() => {
    if (!templateM1) return null;
    return resolveEffectiveAiAnalysis(templateM1, analysisConfig);
  }, [templateM1, analysisConfig]);

  // 初回 ready で draft / overrides / M3 draft を実効値で初期化する。
  useEffect(() => {
    if (initialized) return;
    if (districtState.status !== 'ready') return;
    if (m1State.status !== 'ready' || m3State.status !== 'ready') return;
    if (!templateM1 || !resolved) return;
    const d = draftFromResolved(templateM1, resolved);
    // 初回 ready 時の一度きりの draft 初期化 (external state = Firestore district doc の同期)。
    // initialized ガードで再入を防ぐため cascading render にはならない。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(d);
    setOverrides(overrideStateFromConfig(analysisConfig ?? undefined));
    // M3 fields[] 編集 draft: 解決済み outputFormatId の M3 を起点に。地区 outputFieldsOverride が
    // あればそれを fields[] に反映 (= 地区専用列を初期表示)。
    const m3 = m3Items.find((m) => m.id === (resolved.outputFormatId.value ?? ''));
    if (m3) {
      const baseDraft = m3DraftFromItem(m3);
      const ov = resolved.outputFieldsOverride.value;
      if (ov && ov.length > 0) {
        baseDraft.fields = ov.map(cloneField);
      }
      setM3Draft(baseDraft);
    }
    setInitialized(true);
    dirtySnapshot.current = null; // 次の描画で初期状態を記録する
  }, [
    initialized,
    districtState.status,
    m1State.status,
    m3State.status,
    templateM1,
    resolved,
    analysisConfig,
    m3Items,
  ]);

  const selectedM3 = useMemo(
    () => (draft ? m3Items.find((m) => m.id === draft.outputFormatId) : undefined),
    [draft, m3Items],
  );
  const linkedM2s = useMemo(
    () => (draft ? m2Items.filter((m) => draft.categoryMasterIds.includes(m.id)) : []),
    [draft, m2Items],
  );

  // 保存していない変更の判定 (観点を切り替える前の確認用)。ref の更新だけなので再描画は起こさない。
  useEffect(() => {
    if (!initialized || !draft) return;
    const snap = JSON.stringify({ draft, overrides, m3Draft });
    if (dirtySnapshot.current === null) dirtySnapshot.current = snap;
    onDirtyChange(snap !== dirtySnapshot.current);
  }, [initialized, draft, overrides, m3Draft, onDirtyChange]);

  // 地区モードの postFields: その地区の dataSettings.postFields を優先する (日本語ラベル・入力項目が
  // 地区ごとに異なるため、ユーザー要望)。地区が独自項目を持たない場合は mode 既定テンプレにフォールバック。
  const selectablePostFieldList: FieldDef[] = useMemo(() => {
    if (!draft) return [];
    const districtFields = district?.dataSettings?.postFields;
    const base =
      districtFields && districtFields.length > 0
        ? districtFields
        : getModeTemplate(draft.mode).dataSettings.postFields;
    return selectablePostFields(base);
  }, [draft, district]);
  const selectableKeys = useMemo(
    () => selectablePostFieldList.map((f) => f.key),
    [selectablePostFieldList],
  );
  const checkedInputKeys = useMemo(
    () => (draft ? expandInputFieldKeys(draft.inputFieldKeys, selectableKeys) : []),
    [draft, selectableKeys],
  );

  // 右ペイン preview: outputFields override ON なら M3 draft schema を反映、OFF なら継承 M3 をそのまま。
  const previewM3: (AiOutputFormat & { id: string }) | undefined = useMemo(() => {
    if (!selectedM3) return undefined;
    if (overrides.fields.outputFields && m3Draft && m3Draft.id === selectedM3.id) {
      return { ...selectedM3, schema: m3DraftToSchema(m3Draft) };
    }
    return selectedM3;
  }, [selectedM3, m3Draft, overrides.fields.outputFields]);

  const payloadPreview = useMemo<AiPayloadPreview | null>(() => {
    if (!draft) return null;
    return buildAiPayloadPreview({
      mode: draft.mode,
      systemInstructions: draft.systemInstructions,
      promptSections: draft.promptSections,
      promptInputMapping: draft.promptInputMapping,
      inputFieldKeys: draft.inputFieldKeys,
      generationConfig: draft.generationConfig,
      validatorRules: draft.validatorRules,
      titleGeneration: draft.titleGeneration,
      m2List: linkedM2s,
      m3Schema: previewM3?.schema,
      postFields: selectablePostFieldList,
    });
  }, [draft, linkedM2s, previewM3, selectablePostFieldList]);

  const modeIntegrity = useModeProfileIntegrity(draft?.mode);
  const warnings = useMemo(() => {
    if (!draft) return [] as IntegrityWarning[];
    return checkProfileIntegrity({
      mode: draft.mode,
      m1: {
        outputFormatId: draft.outputFormatId,
        categoryMasterIds: draft.categoryMasterIds,
        validatorRules: draft.validatorRules,
        promptInputMapping: draft.promptInputMapping,
        systemInstructions: draft.systemInstructions,
      },
      m2List: m2Items,
      m3: previewM3,
      // mode 文字列の直接比較ではなく ModeProfile (M10) の推奨構成を正とする (issue #45)
      recommendedMasterIds: modeIntegrity.recommendedMasterIds,
      resolveModeLabel: modeIntegrity.resolveModeLabel,
    });
  }, [draft, m2Items, previewM3, modeIntegrity]);

  // -------- トグルハンドラ --------

  /**
   * 項目トグル ON/OFF。OFF に戻したときは draft の当該項目を実効テンプレ値へ巻き戻す
   * (= 継承表示が正しいテンプレ値になる)。
   */
  function toggleField(key: OverrideFieldKey, next: boolean) {
    if (!draft || !resolved) return;
    setOverrides((prev) => ({ ...prev, fields: { ...prev.fields, [key]: next } }));
    if (next) return;
    // OFF: テンプレ実効値へ巻き戻す。
    switch (key) {
      case 'outputFormatId':
        setDraft({ ...draft, outputFormatId: resolved.outputFormatId.value ?? '' });
        break;
      case 'inputFieldKeys':
        setDraft({
          ...draft,
          inputFieldKeys:
            resolved.inputFieldKeys.value === undefined
              ? undefined
              : [...resolved.inputFieldKeys.value],
        });
        break;
      case 'categoryMasterIds':
        setDraft({ ...draft, categoryMasterIds: [...resolved.categoryMasterIds.value] });
        break;
      case 'generationConfig':
        setDraft({ ...draft, generationConfig: { ...resolved.generationConfig.value } });
        break;
      case 'titleGeneration':
        setDraft({
          ...draft,
          titleGeneration: resolved.titleGeneration.value
            ? { ...resolved.titleGeneration.value }
            : null,
        });
        break;
      case 'validators':
        setDraft({
          ...draft,
          validatorRules: resolved.validators.value.rules
            ? [...resolved.validators.value.rules]
            : [],
        });
        break;
      case 'outputFields':
        // M3 draft の fields[] をテンプレ M3 の fields に巻き戻す。
        if (selectedM3) setM3Draft(m3DraftFromItem(selectedM3));
        break;
    }
  }

  /** プロンプトセクショントグル。OFF で当該セクションをテンプレ実効値へ巻き戻す。 */
  function togglePromptSection(key: keyof AiPromptSections, next: boolean) {
    if (!draft || !resolved) return;
    if (key === 'rules') return; // マスタ専管
    setOverrides((prev) => ({
      ...prev,
      promptSections: { ...prev.promptSections, [key]: next },
    }));
    if (!next) {
      setDraft({
        ...draft,
        promptSections: {
          ...draft.promptSections,
          [key]: resolved.promptSections.value[key] ?? '',
        },
      });
    }
  }

  const dctx: DistrictSectionCtx | undefined = resolved
    ? { overrides, toggleField, togglePromptSection, resolved }
    : undefined;

  // -------- 副ハンドラ (テンプレモードと同等、ただし M1 mode は固定 = 編集不可) --------

  // 地区モードの入力項目選択 (2026-10-08 見直し)。実行時は対応表に無い地区の入力項目も自動で送る
  // (shared-types withPostFieldMappings) ため、undefined (= 全選択) は「地区の入力項目すべて
  // (非表示を除く)」を意味する。全部チェックなら undefined に畳み、地区で項目を追加したときも既定で
  // 送られるようにする。ただしテンプレ (M1) 側が明示リストで絞っている場合は、undefined だと
  // テンプレのリストが効いて地区独自の項目が落ちるので、明示配列で保持する。
  // 編集した時点で inputFieldKeys override を自動 ON にする (OFF のままだと
  // buildDistrictConfigPayload がキーを書かず、保存後テンプレ既定へ巻き戻るバグの修正)。
  // 対応表 (promptInputMapping) は触らない (実行時に自動で足すため)。
  function districtInputKeysValue(selected: string[]): string[] | undefined {
    const all = selected.length === selectableKeys.length;
    return all && templateM1?.inputFieldKeys === undefined ? undefined : selected;
  }
  function enableInputFieldKeysOverride() {
    setOverrides((prev) =>
      prev.fields.inputFieldKeys
        ? prev
        : { ...prev, fields: { ...prev.fields, inputFieldKeys: true } },
    );
  }
  function toggleInputFieldKey(key: string, checked: boolean) {
    if (!draft) return;
    const cur = new Set(checkedInputKeys);
    if (checked) cur.add(key);
    else cur.delete(key);
    const nextSelected = selectableKeys.filter((k) => cur.has(k));
    setDraft({ ...draft, inputFieldKeys: districtInputKeysValue(nextSelected) });
    enableInputFieldKeysOverride();
  }
  function setAllInputFieldKeys(mode: 'all' | 'none') {
    if (!draft) return;
    if (mode === 'all') {
      setDraft({ ...draft, inputFieldKeys: districtInputKeysValue([...selectableKeys]) });
    } else {
      setDraft({ ...draft, inputFieldKeys: [] });
    }
    enableInputFieldKeysOverride();
  }
  function toggleM2(id: string, checked: boolean) {
    if (!draft) return;
    const next = checked
      ? [...draft.categoryMasterIds.filter((x) => x !== id), id]
      : draft.categoryMasterIds.filter((x) => x !== id);
    setDraft({ ...draft, categoryMasterIds: next });
  }
  function setOutputFormatId(id: string) {
    if (!draft) return;
    setDraft({ ...draft, outputFormatId: id });
    const m3 = m3Items.find((m) => m.id === id);
    setM3Draft(m3 ? m3DraftFromItem(m3) : null);
  }

  async function handleSave() {
    if (!draft || !district) return;
    // outputFields override ON のときは M3 fields[] を検証 (key 必須 / 重複)。
    let outputFieldsDraft: AiOutputFieldDef[] | null = null;
    if (overrides.fields.outputFields && m3Draft) {
      const m3Err = validateM3Fields(m3Draft.fields);
      if (m3Err) {
        toast('error', `出力列 fields[]: ${m3Err}`);
        return;
      }
      const schema = m3DraftToSchema(m3Draft);
      outputFieldsDraft = schema.fields ?? [];
    }
    // validators override ON のとき rule 検証。
    if (overrides.fields.validators) {
      const vr = validateRules(draft.validatorRules);
      if (!vr.ok) {
        toast('error', `検証ルール: ${vr.error}`);
        return;
      }
    }
    // generationConfig override ON のとき数値検証。
    let cleanedGc = draft.generationConfig;
    if (overrides.fields.generationConfig) {
      const gc = draft.generationConfig;
      if (!gc) {
        toast('error', 'Gemini パラメータが未設定です');
        return;
      }
      // テンプレ保存と同じく、世代で有効な数値項目だけを検証する。
      const gcNumbers = validateGenerationConfigNumbers(gc);
      if (!gcNumbers.ok) {
        toast('error', gcNumbers.error);
        return;
      }
      // model / thinkingConfig 検証 + 正規化 (テンプレ保存と同じルール)。
      const gcMeta = validateGenerationConfigMeta(gc);
      if (!gcMeta.ok) {
        toast('error', gcMeta.error);
        return;
      }
      cleanedGc = gcMeta.value;
    }

    const config = buildDistrictConfigPayload({
      enabled: district.aiAnalysis?.enabled ?? true,
      settingsId: analysisConfig?.settingsId ?? null,
      draft: {
        outputFormatId: draft.outputFormatId,
        inputFieldKeys: draft.inputFieldKeys,
        categoryMasterIds: draft.categoryMasterIds,
        generationConfig: cleanedGc,
        titleGeneration: draft.titleGeneration,
        validatorRules: draft.validatorRules,
        promptSections: draft.promptSections,
        promptInputMapping: draft.promptInputMapping,
      },
      outputFieldsDraft,
      overrides,
    });

    setSaving(true);
    try {
      if (isDefaultAnalysis) {
        await setDistrictAiAnalysis(district.id, uid, config);
      } else {
        // docs/29 §7-1: 追加の観点は postAnalyses[i] の上書きの項目だけを置き換える。
        await setDistrictPostAnalysisOverrides(district.id, uid, props.analysisKey, config);
      }
      onDirtyChange(false);
      dirtySnapshot.current = JSON.stringify({ draft, overrides, m3Draft });
      toast(
        'success',
        props.analysisLabel
          ? `「${props.analysisLabel}」の地区別 AI 解析設定を保存しました`
          : '地区別 AI 解析設定を保存しました',
      );
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  // -------- render --------

  if (districtState.status === 'loading' || m1State.status === 'loading') {
    return (
      <Card padding="lg">
        <p className="text-sm text-slate-500">読込中…</p>
      </Card>
    );
  }
  if (districtState.status === 'not-found') {
    return (
      <Card padding="lg">
        <p className="text-sm text-danger-700">地区が見つかりません ({props.districtId})。</p>
        <Link to="/districts" className="text-sm text-primary-600 hover:underline">
          → 地区一覧へ戻る
        </Link>
      </Card>
    );
  }
  if (districtState.status === 'error') {
    return (
      <Card padding="lg">
        <p className="text-sm text-danger-700">地区の読込に失敗しました。</p>
      </Card>
    );
  }
  if (!district) return null;

  // 参照テンプレ (M1) 未選択 / 未解決: 編集できないため案内する。
  if (!templateM1 || !draft || !dctx) {
    return (
      <div>
        <header className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">AI 解析 統合エディタ（地区モード）</h1>
          <p className="text-sm text-slate-500 mt-1">
            この地区: <strong>{district.name}</strong> ({district.id})
          </p>
          {props.tabs}
        </header>
        <Card padding="lg" className="border-amber-200 bg-amber-50">
          <p className="text-sm text-amber-800">
            この地区にはまだ参照テンプレ (AI 解析設定 M1) が紐付いていません。先に
            <Link to={`/districts/${district.id}/ai`} className="text-primary-700 underline mx-1">
              地区の AI 解析パネル
            </Link>
            で「使用する AI 解析設定」を選択してください（追加の観点は「AI
            解析の観点」パネルで解析設定を選びます）。
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900">AI 解析 統合エディタ（地区モード）</h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-emerald-800">
            この地区: <strong>{district.name}</strong>
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-600">
            参照テンプレ: <strong>{templateM1.displayName}</strong> ({templateM1.id})
          </span>
          {props.analysisLabel && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-sky-50 border border-sky-200 text-sky-800">
              観点: <strong>{props.analysisLabel}</strong>
            </span>
          )}
        </div>
        {props.tabs}
        <p className="text-xs text-slate-500 mt-2">
          編集対象は<strong>この地区だけ</strong>
          です（テンプレ＝全地区には影響しません）。各項目は既定で
          テンプレ継承（淡色グレー・編集不可）で、「✓この地区で上書き」をオンにするとこの地区専用の値を
          編集できます。<code>rules</code> セクションはマスタ専管（🔒 上書き不可）です。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(360px,40%)] gap-4 items-start">
        {/* ============ LEFT: accordion sections (地区モード = dctx 付与) ============ */}
        <div className="grid grid-cols-1 gap-3">
          <SectionGenerationConfig draft={draft} setDraft={setDraft} dctx={dctx} />
          <SectionPrompt
            draft={draft}
            setDraft={setDraft}
            onConvertLegacy={() => undefined}
            onDeleteLegacy={() => undefined}
            dctx={dctx}
          />
          <SectionInputData
            draft={draft}
            fields={selectablePostFieldList}
            checkedKeys={checkedInputKeys}
            onToggle={toggleInputFieldKey}
            onAll={() => setAllInputFieldKeys('all')}
            onNone={() => setAllInputFieldKeys('none')}
            dctx={dctx}
          />
          <SectionCategoryMasters draft={draft} m2Items={m2Items} onToggle={toggleM2} dctx={dctx} />
          <SectionOutput
            draft={draft}
            m3Items={m3Items}
            m3Draft={m3Draft}
            setM3Draft={setM3Draft}
            onSelect={setOutputFormatId}
            dctx={dctx}
          />
          <SectionValidators
            draft={draft}
            setDraft={setDraft}
            m2Items={m2Items}
            selectedM3={selectedM3}
            dctx={dctx}
          />
          <SectionTitleGeneration draft={draft} setDraft={setDraft} dctx={dctx} />

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            <Link
              to={`/districts/${district.id}/ai`}
              className="text-sm text-slate-500 hover:underline self-center"
            >
              ← 地区 AI パネルへ戻る
            </Link>
            <Button variant="primary" size="md" onClick={() => void handleSave()} disabled={saving}>
              {saving ? '保存中…' : 'この地区の設定を保存'}
            </Button>
          </div>
        </div>

        {/* ============ RIGHT: sticky live preview (実効値ベース) ============ */}
        <div className="lg:sticky lg:top-4 grid grid-cols-1 gap-3">
          <PreviewWarnings warnings={warnings} mode={draft.mode} />
          <PreviewPayload preview={payloadPreview} />
        </div>
      </div>
    </div>
  );
}
